
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import moment from 'moment';

import TableSelection from './TableSelection';
import OrderScreen from './OrderScreen';
import BillingScreen from './BillingScreen';

import axios from '../../../axios-url';
import Messages from '../../../shared/UI/Messages/Messages';
import * as actions from '../../../store/actions/index';

import { F, raw } from './RestaurantOrderFields';
import { buildStageFields, buildMenuItemRow, buildKotInfoRow, buildPaymentRow } from './RestaurantOrderState';
import {
    updateContainerFieldValue, addContainerRow, removeContainerRow,
} from './containerHelpers';
import { ORDER_TYPES } from '../../Layout/POSSidebar';

import PrintPreviewModal from './PrintPreviewModal';
import { buildKotPrintData, buildBillPrintData } from './printBuilders';
import { openRazorpayCheckout } from './razorpayCheckout';
import razorpayAxios from './razorpay-axios';
// TODO: register in shared/Config.js the same way LEAVE_REQUEST_ID is.
// This is the real processId from the schema.
const RESTAURANT_ORDER_ID = 'f7405b60-9933-4b08-a8be-107aa21a05f7';
const PROCESS_NUM = '303';
const STAGE_NUM = '001'; // matches stageNum in the schema (stage 0 -> "001")
const ZERO_GUID = '00000000-0000-0000-0000-000000000000';

// A date value as a Date, or null when it is empty or can't be parsed.
// The backend returns an unset date field as "" and new Date("") is an Invalid
// Date object, which is truthy, so it got past every `value || fallback` and
// moment().format() then turned it into the literal "Invalid date". Sent as
// resdeliverydate that fails the whole save ("Could not convert string to
// DateTime"), which is what stopped Generate Bill on takeaway orders.
const toValidDate = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

// requestaction print flags, both nvarchar(10):
//   printingrequired = "this needs printing"  (set here)
//   isprinted        = "it has been printed"  (set by the print service only)
// Strings, not booleans — the older rdr/rdnr bit columns are left alone.
// Only two places switch printingrequired on: a KOT actually sent to the
// kitchen, and a bill at the moment it is generated.
const PRINT_FLAG_ON = '1';
const PRINT_FLAG_OFF = '0';

// Backend-echoed rows (e.g. requestActionsRaw, from data.requestactions on a
// GET) don't come back in a consistent key casing — MenuId/menuId/menuid all
// show up depending on the endpoint. Locally-built rows are always the exact
// PascalCase we chose in _buildBaseActionRow, so this is only ever needed
// when reading something that came back from the server. See the identical
// helper in QrOrderDetails.js.
const pick = (obj, ...keys) => {
    for (const key of keys) {
        if (obj[key] !== undefined && obj[key] !== null) return obj[key];
    }
    return undefined;
};

// Union existingRaw (last known requestactions) with freshRaw (this GET's
// data.requestactions) keyed on the backend's own requestactionid —
// casing-tolerant via pick(), same reason pick() exists. A fresh entry with
// an id already seen replaces the old copy (server is authoritative for that
// row); an id not seen before is appended; a previously-known row whose id is
// simply absent from this response (the response the GET right after our own
// Insert/UpdateDynamicData raced, same reasoning as the stageFields merge
// below) is kept, not silently dropped. Rows with no id at all are always
// appended — see the plan file's noted caveat if that ever produces
// duplicates in practice.
const idOf = (row) => pick(row, 'RequestActionId', 'requestActionId', 'requestactionid');
const mergeRequestActionsRaw = (existingRaw, freshRaw) => {
    const existingList = Array.isArray(existingRaw) ? existingRaw : [];
    const freshList = Array.isArray(freshRaw) ? freshRaw : [];
    const freshById = new Map();
    freshList.forEach(row => { const id = idOf(row); if (id !== undefined) freshById.set(id, row); });
    const knownIds = new Set(existingList.map(idOf).filter(id => id !== undefined));
    const merged = existingList.map(row => {
        const id = idOf(row);
        return (id !== undefined && freshById.has(id)) ? freshById.get(id) : row;
    });
    freshList.forEach(row => {
        const id = idOf(row);
        if (id === undefined || !knownIds.has(id)) merged.push(row);
    });
    return merged;
};

// Table Master (restable) status values — see AddTableMaster.js statusOptions.
const TABLE_STATUS_AVAILABLE = 1;
const TABLE_STATUS_OCCUPIED = 2;
// Set the instant a free table is picked, before any order exists — value 3
// used to be "Reserved" but that was never wired up anywhere, so it's been
// repurposed as the transient pre-order lock state instead.
const TABLE_STATUS_LOCKED = 3;

// ── ChildRowNmbr 0 is the container's blank template row ────────────────────
// The process definition ships every container with one row of child fields at
// ChildRowNmbr 0; real data rows are 1..N. RequestAttributesDetails hands that
// template row back inside stage_FieldsList together with the data rows.
//
// A row keeps the number the backend gave it for its whole life and is matched
// on that number - the same thing LeaveRequest does, which reads childRowNmbr
// on load (LeaveRequest.js:3096) and sends the identical value back on save
// (LeaveRequest.js:1116) rather than renumbering rows by array position.
const getChildRowNmbr = (containerField) => {
    const containerFField = containerField && Object.values(containerField).find(
        f => f && typeof f === 'object' && f.ChildRowNmbr !== undefined && f.ChildRowNmbr !== null
    );
    return containerFField ? Number(containerFField.ChildRowNmbr) : NaN;
};

// A row we cannot number is treated as data, never as the template - an
// unreadable row is kept, not silently swallowed.
const isTemplateRow = (containerField) => getChildRowNmbr(containerField) === 0;

// One past the highest number in use, so a mid-list delete leaves a gap rather
// than handing the next row a number that is still taken. (LeaveRequest uses
// selectedContainer.length + 1, which collides after a delete.)
const getIncrementRow = (containerFields = []) => {
    const childRowNmbrs = containerFields.map(getChildRowNmbr).filter(n => Number.isFinite(n));
    return (childRowNmbrs.length > 0 ? Math.max(...childRowNmbrs) : 0) + 1;
};

// Stamps a freshly built row with its number, once, at creation - the same
// point LeaveRequest.addContainerField assigns incrementRow. Every field of the
// row carries it, because that is what goes over the wire per attribute.
const setChildRowNmbr = (containerField, containerFields) => {
    const incrementRow = getIncrementRow(containerFields);
    const newValue = {};
    Object.keys(containerField).forEach(containerFKey => {
        const containerFField = containerField[containerFKey];
        newValue[containerFKey] = (containerFField && typeof containerFField === 'object' && containerFField.FieldName)
            ? { ...containerFField, ChildRowNmbr: incrementRow }
            : containerFField;
    });
    return newValue;
};

class RestaurantOrder extends Component {

    state = {
        // ── POS view/session state ─────────────────────────────────────
        view: this.props.activeOrderType === ORDER_TYPES.DINE_IN ? 'tables' : 'order',
        selectedTable: null,
        tableSessions: {},
        // Table (from the grid) whose live status a click is currently
        // re-checking before locking it — see handleTableSelect/_resolveTableCheck.
        checkingTable: null,

        // ── Backend-contract field state (mirrors the real schema) ──────
        stageFields: buildStageFields(this.props.activeOrderType),
        requestActionsRaw: [],

        // ── Workflow state ────────────────────────────────────────────
        type: 'add',
        loading: false,
        reqStatus: null,
        requestNum: null,
        DynamicRequestID: null,
        loadOnce: true,
        billingSubmitting: false,
        DynamicRequestID: null,
        sessionKey: null,
        runningOrders: [],
        printModal: null,
        // ── Save queued while a previous save + FieldId-rehydration was in flight ──
        pendingSave: null,
        // ── Generate Bill just wrote fresh totals into Bill Summary and is about
        // to trigger the FieldId re-fetch every save kicks off. That GET can beat
        // the backend committing the write it is chasing (see the big comment in
        // the rehydration reconcile below), coming back with the pre-bill values
        // and blanking the billing screen the instant it lands. Sits true for
        // exactly one rehydration cycle so the reconcile knows to keep the local
        // Bill Summary row (only its FieldId is worth taking from the response),
        // then clears itself.
        protectBillSummaryOnce: false,
        // Live GetresActionStatusflow rows (see fetchResStatusMaster); null until loaded.
        resStatusMaster: null,
    }

    componentDidMount() {
        this.fetchRunningOrders();
        this.fetchResStatusMaster();
        // Keyword lists needed for the dropdowns below. Swap these for your
        // real keyword names once confirmed (Branch, Waiter, Cashier, Shift,
        // MenuItem, UOM, Kitchen, DeliveryBoy, PaymentType, ActionStatus...).
        this.props.getMasterData("res/GetinvMenuItemList?companyId=" + this.props.loggedUserDetail.CompanyId);
        const lists = ['Modifier', 'MenuCategory', 'Employee', 'Shift', 'MenuItem', 'UOM', 'Kitchen', 'DeliveryBoy', 'PaymentType', 'ActionStatus', 'TableStatus'];
        lists.forEach(l => this.props.getMasterKeywordList && this.props.getMasterKeywordList(l));
        this.props.getMasterData("res/GetresTableList?companyId=" + this.props.loggedUserDetail.CompanyId);


        const requestIdFromRoute = this.props.match && this.props.match.params && this.props.match.params.requestid;
        if (requestIdFromRoute) {
            this._loadOrderFromRoute(requestIdFromRoute);
        }

        // this.props.getMasterData("res/GetrestableOrderNum?BranchId="+this.props.loggedUserDetail.defaultbranch,this.props.loggedUserDetail.CompanyId);
        // this.props.getMasterData("res/GetresTableList?companyId=" + this.props.loggedUserDetail.CompanyId);

        const type = this.props.match && this.props.match.params && this.props.match.params.type;
        if (type && type !== 'add') {
            const id = this.props.match.params.id;
            this.setState({ type, loading: true, pendingRequestId: id });
            localStorage.removeItem('Employee/RequestAttributesDetails?Id=' + id);
            this.props.getMasterTableList && this.props.getMasterTableList('Employee/RequestAttributesDetails?Id=' + id);
        }
    }

    // ── Fetch-and-rehydrate for a requestId that just became the URL — used
    // both on a cold open (componentDidMount, route already carries it) and
    // when handleTableSelect navigates to /pos/order/:requestid for a running
    // order instead of loading it in place. Shared so both paths hydrate
    // stageFields the exact same way. ───────────────────────────────────────
    _loadOrderFromRoute = (requestId) => {
        // Whatever the store holds right now was fetched earlier — possibly for
        // this very order, which the requestId check in componentDidUpdate can't
        // tell apart from the answer to the fetch below. Remember it so only a
        // response that arrives after this point is rehydrated from.
        this._staleOrderResponse = this.props.tableLists;
        this.setState({
            loading: true,
            loadOnce: true,
            pendingRequestId: requestId,
            openedViaDirectLink: true,
            view: 'order',
            pendingSave: null,
            // This is always a cold fetch for the order the URL now names, never
            // a post-bill refresh — a stuck flag from an abandoned 'bill' save on
            // whatever was open before must not leak into this order's rehydration.
            protectBillSummaryOnce: false,
        });
        // ── Same cache-bust LeaveRequest.fetchSingleProcess uses: clear the exact
        // localStorage key getMasterTableList checks before re-fetching it. ──────
        localStorage.removeItem('Employee/RequestAttributesDetails?Id=' + requestId);
        this.props.getMasterTableList &&
            this.props.getMasterTableList('Employee/RequestAttributesDetails?Id=' + requestId);
    };

    componentDidUpdate(prevProps, prevState) {

        // ── Sidebar switched order type — clear every bit of this order's
        // state rather than reloading the page (reload was too slow — a full
        // navigation + JS reload + re-fetch of every keyword/master list just
        // to blank out one screen). loadOnce is reset to false, not true: there
        // is no fetch in flight for the new type, so leaving it true let a
        // stale tableLists response the type just left behind rehydrate
        // straight onto this blank state the moment anything re-rendered —
        // the same race the old "reset to true" version of this block had. ──
        if (prevProps.activeOrderType !== this.props.activeOrderType) {
            this.props.clearCacheStorage && this.props.clearCacheStorage('tableLists', null);
            this.setState({
                view: this.props.activeOrderType === ORDER_TYPES.DINE_IN ? 'tables' : 'order',
                selectedTable: null,
                tableSessions: {},
                checkingTable: null,
                stageFields: buildStageFields(this.props.activeOrderType),
                requestActionsRaw: [],
                DynamicRequestID: null,
                pendingRequestId: null,
                loadOnce: false,
                loading: false,
                pendingSave: null,
                reqStatus: null,
                requestNum: null,
                printModal: null,
                protectBillSummaryOnce: false,
            }, () => this.fetchRunningOrders());
        }

        // ── URL now names a different order (handleTableSelect navigated to
        // /pos/order/:requestid for a running order, or the user hit back/forward
        // between two such URLs) — fetch and rehydrate it. RestaurantOrder stays
        // mounted across this route change (both "/pos/order/:requestid" and
        // "/pos/" resolve to the same component in POSApp's Switch), so this is
        // the only place besides componentDidMount that can catch it. ──────────
        const prevRouteRequestId = prevProps.match && prevProps.match.params && prevProps.match.params.requestid;
        const currRouteRequestId = this.props.match && this.props.match.params && this.props.match.params.requestid;
        if (currRouteRequestId && currRouteRequestId !== prevRouteRequestId) {
            this._loadOrderFromRoute(currRouteRequestId);
        }

        // ── Rehydrate from backend response ──────────────────────────────────
        // Mirrors LeaveRequest.componentDidUpdate which checks:
        //   this.props.tableLists.requestAttribute1 !== undefined && this.state.loadOnce
        // then rebuilds stages[] from the response.
        // Here we rebuild stageFields instead of stages[].
        if (
            this.props.tableLists &&
            this.props.tableLists.requestAttribute1 !== undefined &&
            this.props.tableLists !== this._staleOrderResponse &&
            this.state.loadOnce
        ) {
            const data = this.props.tableLists;
            const responseRequestId = data.requestId || data.requestID || data.requestid || null;

            if (this.state.pendingRequestId) {
                if (!responseRequestId || this.state.pendingRequestId.toLowerCase() !== responseRequestId.toLowerCase()) {
                    // Stale data from previous fetch or missing IDs, ignore it and wait for the correct one
                    return;
                }
            }

            // ── Start with a fresh blank stageFields (same as LeaveRequest
            //    starting with its hardcoded stage definitions) ────────────────
            let rehydratedFields = buildStageFields();

            // Containers the response actually carried data rows for. The rest
            // still hold buildStageFields()' blank starter rows, which must not be
            // mistaken for the backend's answer during the reconcile below.
            const rehydratedContainerKeys = new Set();

            // ── Walk requestAttribute1[0].stage_Field (RO has one stage,
            //    LeaveRequest walks all stages) ─────────────────────────────────
            if (data.requestAttribute1 && data.requestAttribute1.length > 0) {
                const stageList = data.requestAttribute1[0];
                const stageFieldsRaw = stageList.stage_Field || [];

                // ── De-duplicate the same way LeaveRequest does: key each field by
                // identity and collapse with Object.assign — a later duplicate just
                // overwrites the earlier one under the same key. Needed here because
                // the backend appears to join attributes against RequestActions (one
                // row per KOT/workflow action taken on this request), which fans out
                // and repeats every attribute once per action row.
                const stageFieldsByKey = {};
                stageFieldsRaw.forEach(field => {
                    const key = (field.fieldId && field.fieldId !== ZERO_GUID)
                        ? field.fieldId
                        : `${field.fieldName}:${field.childRowNmbr}`;
                    stageFieldsByKey[key] = field;
                });
                const stageFields = Object.values(stageFieldsByKey);

                stageFields.forEach(field => {
                    // LeaveRequest does: let fieldsY = 'field_' + fields.fieldName
                    const fieldsY = 'field_' + field.fieldName;

                    // LeaveRequest does date/boolean casting:
                    let fieldValue = field.fieldValue;
                    if (field.fieldType === 8 || field.fieldType === '8') {
                        // null rather than an Invalid Date for an empty field.
                        fieldValue = toValidDate(field.fieldValue);
                    } else if (field.fieldType === 10 || field.fieldType === '10') {
                        fieldValue = field.fieldValue === 'true';
                    }

                    // Update flat field if it exists in our schema
                    if (rehydratedFields[fieldsY] !== undefined) {
                        rehydratedFields[fieldsY] = {
                            ...rehydratedFields[fieldsY],
                            FieldValue: fieldValue,
                            FieldId: field.fieldId || '',
                            Id: field.id || 0,
                            // Keep the backend's own row number (LeaveRequest.js:3123)
                            // instead of overwriting it with mkField's default of 1 on
                            // the next save.
                            ChildRowNmbr: (field.childRowNmbr !== undefined && field.childRowNmbr !== null)
                                ? field.childRowNmbr
                                : rehydratedFields[fieldsY].ChildRowNmbr,
                        };
                    }

                    // ── Container fields ──────────────────────────────────────
                    // LeaveRequest does: fields.stage_FieldsList.map(conFields =>
                    //   conFields.stage_FieldsListc2.map(conField => ...))
                    if (
                        field.stage_FieldsList &&
                        field.stage_FieldsList.length > 0 &&
                        rehydratedFields[fieldsY] !== undefined
                    ) {
                        // ── Same join fan-out, one level down, deduped the same
                        // Object.assign-by-key way: a whole row (e.g. a Menu Item row)
                        // can repeat verbatim once per workflow action, all its child
                        // fields sharing the same fieldIds. Key each row by the
                        // signature of its children's fieldIds and collapse — otherwise
                        // every KOT/action taken on this order doubles the number of
                        // menu-item rows that get sent back on the next save.
                        const stageFieldsListByKey = {};
                        field.stage_FieldsList.forEach(conFields => {
                            const children = conFields.stage_FieldsListc2 || [];
                            const rowKey = children
                                .map(c => (c.fieldId && c.fieldId !== ZERO_GUID) ? c.fieldId : `${c.fieldNum}:${c.childRowNmbr}`)
                                .join('|');
                            stageFieldsListByKey[rowKey] = conFields;
                        });
                        const dedupedStageFieldsList = Object.values(stageFieldsListByKey);

                        const containerRows = dedupedStageFieldsList.map(conFields => {
                            const rowObj = {};
                            (conFields.stage_FieldsListc2 || []).forEach(conField => {
                                const childKey = 'field_' + conField.fieldNum;

                                let childValue = conField.fieldValue;
                                if (conField.fieldType === 8 || conField.fieldType === '8') {
                                    childValue = toValidDate(conField.fieldValue);
                                } else if (conField.fieldType === 10 || conField.fieldType === '10') {
                                    childValue = conField.fieldValue === 'true';
                                }

                                rowObj[childKey] = {
                                    FieldName: conField.fieldNum,
                                    FieldLabel: conField.fieldDescription,
                                    FieldValue: childValue,
                                    FieldType: conField.fieldType,
                                    DataType: conField.dataType,
                                    ParentFieldId: conField.parentFieldNum,
                                    ChildRowNmbr: conField.childRowNmbr,
                                    FieldId: conField.fieldId || '',
                                    Id: conField.id || 0,
                                    type: conField.containerType,
                                };
                            });
                            // ── Resolve __displayName for Menu Items container ────────────────
                            // fieldDescription "Menu Items" → look up item name from menuitems prop
                            if (field.fieldDescription === 'Menu Items') {
                                const menuItemId = conFields.stage_FieldsListc2?.find(
                                    f => f.fieldDescription === 'Menu Item'
                                )?.fieldValue;

                                if (menuItemId) {
                                    const menuItemsList = this.props.menuitems || [];
                                    const found = menuItemsList.find(
                                        m => m.menuItemId === menuItemId
                                    );
                                    rowObj.__displayName = found
                                        ? (found.menuName)
                                        : menuItemId;
                                }
                            }
                            return rowObj;
                        });

                        // ── Park the ChildRowNmbr 0 template row aside ────────────
                        // It has to go back to the backend on save with its number
                        // intact, but it is not order data: leaving it in
                        // ContainerFields put a blank row at index 0, which is the row
                        // every single-row container reads as "the" row (bill summary,
                        // table info, customer info) - that is why the billing screen
                        // came up empty on an order that already had a bill.
                        const templateContainerField = containerRows.find(isTemplateRow);
                        const containerFields = containerRows.filter(row => !isTemplateRow(row));

                        if (containerFields.length > 0) rehydratedContainerKeys.add(fieldsY);

                        rehydratedFields[fieldsY] = {
                            ...rehydratedFields[fieldsY],
                            // A response carrying nothing but the template leaves the
                            // blank starter row from buildStageFields in place, so
                            // ContainerFields[0] is never undefined for the single-row
                            // containers the screens index into.
                            ...(containerFields.length > 0 ? { ContainerFields: containerFields } : {}),
                            TemplateContainerField: templateContainerField
                                || rehydratedFields[fieldsY].TemplateContainerField
                                || null,
                        };
                    }
                });
            }

            // ── Reconcile container rows against what's already on screen ──────
            // The GET right after our own Insert/UpdateDynamicData call can race the
            // backend committing/indexing that write, coming back short (or with a
            // container's stage_FieldsList missing entirely, which leaves the loop
            // above at the blank template's []). An all-or-nothing fallback here is
            // wrong too: it would permanently block FieldId sync for any row it falls
            // back on, since local rows never gain a FieldId on their own — that's
            // exactly what produced rows with no FieldId (and duplicate inserts) a
            // few rows in. Instead merge per-row on ChildRowNmbr: the server's copy of
            // a row wins (so FieldId lands in state), and a local row the read hasn't
            // caught up to yet is kept on the end — it keeps its number and syncs on
            // the next save.
            Object.keys(rehydratedFields).forEach(fieldsY => {
                const rehydratedField = rehydratedFields[fieldsY];
                if (!rehydratedField || !Array.isArray(rehydratedField.ContainerFields)) return;

                const localRows = this.state.stageFields[fieldsY]?.ContainerFields || [];
                if (localRows.length === 0) return; // cold open — nothing local to preserve

                // 'bill' just wrote a fresh total into Bill Summary, and this may be
                // the immediate follow-up GET for that very save — see
                // protectBillSummaryOnce above. That response can race the write it
                // is chasing, coming back with pre-bill values; only the FieldId is
                // worth taking from it here, since the local value is what this save
                // just told the backend to persist and cannot be staler than a
                // response racing that same write.
                if (fieldsY === F.BILL_SUMMARY && this.state.protectBillSummaryOnce) {
                    const serverRows = rehydratedContainerKeys.has(fieldsY) ? rehydratedField.ContainerFields : [];
                    rehydratedFields[fieldsY] = {
                        ...rehydratedField,
                        ContainerFields: localRows.map(localRow => {
                            const serverRow = serverRows.find(
                                row => getChildRowNmbr(row) === getChildRowNmbr(localRow)
                            );
                            if (!serverRow) return localRow;
                            const merged = { ...localRow };
                            Object.keys(merged).forEach(key => {
                                const localF = merged[key];
                                const serverF = serverRow[key];
                                if (localF && typeof localF === 'object' && serverF && typeof serverF === 'object') {
                                    merged[key] = {
                                        ...localF,
                                        FieldId: serverF.FieldId || localF.FieldId,
                                        Id: serverF.Id || localF.Id,
                                    };
                                }
                            });
                            return merged;
                        }),
                    };
                    return;
                }

                // The response carried no rows for this container, so what sits in
                // rehydratedFields is buildStageFields()' blank starter row, not an
                // answer. Keep what is on screen — otherwise anything typed into a
                // not-yet-saved container is wiped by the next unrelated save.
                if (!rehydratedContainerKeys.has(fieldsY)) {
                    rehydratedFields[fieldsY] = { ...rehydratedField, ContainerFields: localRows };
                    return;
                }

                const serverChildRowNmbrs = new Set(rehydratedField.ContainerFields.map(getChildRowNmbr));
                const unsyncedContainerFields = localRows.filter(
                    localRow => !serverChildRowNmbrs.has(getChildRowNmbr(localRow))
                );

                rehydratedFields[fieldsY] = {
                    ...rehydratedField,
                    ContainerFields: [...rehydratedField.ContainerFields, ...unsyncedContainerFields],
                };
            });

            // ── Mark menu rows the server already has a KOT action for ─────────
            // Server truth, from the row's own rehydrated MI_KOT_STATUS — anything
            // beyond Pending already has an action row, so the 'kitchen' branch's
            // sentRows filter (below) must not re-declare it on a later send. Same
            // reasoning as QrOrderDetails.js's __kotActionSent. Runs after the merge
            // above so it also covers any locally-unsynced rows carried forward.
            (rehydratedFields[F.MENU_ITEMS]?.ContainerFields || []).forEach(row => {
                const kotStatus = row[F.MI_KOT_STATUS]?.FieldValue;
                row.__kotActionSent = !!(kotStatus && kotStatus !== 'Pending');
            });

            // ── Pick up the requestId from the response ───────────────────────
            // LeaveRequest reads: this.props.tableLists.requestID
            // Use the same field — confirmed from your API response
            const requestId = data.requestId || data.requestID || data.requestid || null;
            // ── Order number comes from requestNum at root level, not from stage_Field ──
            // field 003001000003 (Order Number) is empty string in the API response.
            // Use requestNum directly from the response root.
            if (data.requestNum && rehydratedFields[F.ORDER_NUMBER] !== undefined) {
                rehydratedFields[F.ORDER_NUMBER] = {
                    ...rehydratedFields[F.ORDER_NUMBER],
                    FieldValue: data.requestNum,
                };
            }

            const { selectedTable } = this.state;

            // ── Store rehydrated state and switch to order screen ─────────────
            // LeaveRequest does: this.setState({ stages, loadOnce: false, ... })
            // We do the same but for stageFields and also flip view to 'order'
            this.setState(prev => ({
                loadOnce: false,
                loading: false,
                stageFields: rehydratedFields,
                // Consumed for this one rehydration cycle — clear it so the next,
                // unrelated GET goes back to trusting the server for Bill Summary.
                protectBillSummaryOnce: false,
                requestActionsRaw: mergeRequestActionsRaw(prev.requestActionsRaw, data.requestactions || []),
                DynamicRequestID: requestId,
                reqStatus: data.status,
                requestNum: data.requestNum,
                // ── Only an order being opened lands on the order screen ───────
                // This runs on two different occasions: a cold open (a running
                // order picked off the table screen, or a direct link), which does
                // need to leave the table list, and the FieldId re-fetch that every
                // save kicks off, which must not move the user at all. Forcing
                // 'order' unconditionally meant Generate Bill switched to the
                // billing screen and was then yanked straight back here the moment
                // its own save came back.
                view: prev.view === 'tables' ? 'order' : prev.view,
                //   (no router navigation needed)
                pendingRequestId: null,
                tableSessions: selectedTable
                    ? {
                        ...prev.tableSessions,
                        [selectedTable.tableId]: {
                            startTime: Date.now(),
                            stageFields: rehydratedFields,
                            DynamicRequestID: requestId,
                            status: 'running',
                        },
                    }
                    : prev.tableSessions,
            }), this._replayPendingSave);
        }

        // ── Live table-status check kicked off by handleTableSelect just landed
        // (or failed) — checkingTable is only set while that GET is in flight, so
        // this never fires for an unrelated tableLists update, and the tableId
        // match guards against a stale echo for a different table's check. This
        // response has no requestAttribute1, so it can never be mistaken for the
        // order-rehydration block above, or vice versa. ────────────────────────
        if (this.state.checkingTable && this.props.tableLists !== prevProps.tableLists) {
            const rows = Array.isArray(this.props.tableLists) ? this.props.tableLists : [this.props.tableLists];
            const liveTable = rows.find(r => r && r.tableId === this.state.checkingTable.tableId);
            if (liveTable) {
                this._resolveTableCheck(this.state.checkingTable, liveTable);
            }
        }
        if (this.state.checkingTable && this.props.error && this.props.error !== prevProps.error) {
            this.setState({ checkingTable: null });
            this.growl && this.growl.show({ severity: 'error', summary: null, detail: 'Could not verify table status. Please try again.' });
        }
    }

    // Updates the Table Master (restable) status field for one table, instead of
    // locking the table by inserting a request row. Sends the full table record
    // (same shape AddTableMaster.js's add() sends for an edit) with only status
    // overridden, since the backend update replaces the whole record.
    updateTableMasterStatus = (table, status) => {
        if (!table || !table.tableId) return;
        this.props.processDynamicData('restable', 'edit', { ...table, status }, '');
        // masterData.GetresTableList is cached in localStorage by getMasterData,
        // so a plain re-call would just replay the stale cached list. Clear it
        // first, and wait for the write above to land before refetching.
        setTimeout(() => {
            localStorage.removeItem('MasterData-GetresTableList');
            this.props.getMasterData('res/GetresTableList?companyId=' + this.props.loggedUserDetail.CompanyId);
        }, 2000);
    };

    fetchRunningOrders = () => {
        axios.get("/master/api/res/GetrestableOrderNum?BranchId=00000000-0000-0000-0000-000000000001" + "&CompanyId=" + this.props.loggedUserDetail.CompanyId)
            .then(response => {
                if (response.status === 200 && Array.isArray(response.data)) {
                    this.setState({ runningOrders: response.data });
                }
            })
            .catch(err => console.error('Failed to fetch running orders', err));
    };
    handleTableSelect = (table, runningOrder = null) => {
        const { tableSessions } = this.state;
        const session = tableSessions[table.tableId];

        // ── Already open in this browser session (either an unsaved draft,
        // 'locked', or a saved order, 'running') → resume it directly, no
        // network round trip. A live re-check here would just see our own
        // Locked/Occupied status on the backend and wrongly report the table
        // as taken by someone else. ───────────────────────────────────────
        // A saved order is the exception: another device (CaptainDashboard, a
        // second POS) may have billed or paid it since this copy was taken, so
        // it is re-read through the same URL path a running order uses below.
        // The cached fields go on screen first, so the rehydration's reconcile
        // keeps any cart rows not sent to the kitchen yet.
        if (session && session.DynamicRequestID) {
            this.setState({ selectedTable: table, stageFields: session.stageFields, pendingSave: null });
            this.props.history.push(`/pos/order/${session.DynamicRequestID}`);
            return;
        }
        if (session) {
            this.setState({
                selectedTable: table,
                view: 'order',
                stageFields: session.stageFields,
                DynamicRequestID: session.DynamicRequestID || null,
                pendingSave: null,
            });
            return;
        }

        // ── Running order exists on backend → navigate to its URL instead of
        // fetching it in place. componentDidUpdate's route-change effect calls
        // _loadOrderFromRoute for us once the URL lands, the same way a direct
        // link/refresh/back-forward on /pos/order/:requestid does — one fetch
        // path instead of two. ────────────────────────────────────────────────
        if (runningOrder && runningOrder.requestId) {
            this.setState({ selectedTable: table, pendingSave: null });
            this.props.history.push(`/pos/order/${runningOrder.requestId}`);
            return;
        }

        // ── No session, no running order → possibly fresh. The `tables` list
        // this click was rendered from can be stale (cached GetresTableList) —
        // exactly the window a second waiter could have locked/occupied this
        // same table a moment ago. Re-check the Table Master's live status first
        // (getMasterTableListCache always hits the network, no cache) and only
        // lock it if that live read still says Available. checkingTable blocks
        // re-entrant clicks while the check is in flight; componentDidUpdate
        // picks the response (or failure) up and calls _resolveTableCheck. ────
        if (this.state.checkingTable) return;
        this.setState({ checkingTable: table });
        this.props.getMasterTableListCache(
            'res/GetresTablebyid?TableId=' + table.tableId + '&companyId=' + this.props.loggedUserDetail.CompanyId
        );
    };

    // Continuation of handleTableSelect's fresh-table branch once the live
    // status check (kicked off there) lands. Blocks and refreshes the grid if
    // someone else beat this click to the table; otherwise builds the same
    // fresh order state and locks the table exactly as before.
    _resolveTableCheck = (table, liveTable) => {
        this.setState({ checkingTable: null });
        const liveStatus = Number(liveTable.status);

        if (liveStatus !== TABLE_STATUS_AVAILABLE) {
            this.growl && this.growl.show({ severity: 'warn', summary: null, detail: 'This table was just taken by another user — pick a different table.' });
            localStorage.removeItem('MasterData-GetresTableList');
            this.props.getMasterData('res/GetresTableList?companyId=' + this.props.loggedUserDetail.CompanyId);
            return;
        }

        // Locked on the backend immediately (updateTableMasterStatus below flips
        // the Table Master's status to Locked), before a single item is added,
        // so a second waiter picking this same table right after sees it already
        // locked instead of quietly starting a second order on it. The request
        // itself is not inserted here — it's created later, the first time
        // saveRequest('submit', 'kitchen'/'bill'/'payment') actually runs, which
        // is also what flips the status on from Locked to Occupied.
        this.setState(prev => {
            const freshFields = buildStageFields();
            if (freshFields[F.TABLE_INFO]?.ContainerFields?.[0]) {
                freshFields[F.TABLE_INFO].ContainerFields[0][F.TABLE_NUMBER] = {
                    ...freshFields[F.TABLE_INFO].ContainerFields[0][F.TABLE_NUMBER],
                    FieldValue: table.tableNo,
                };
                freshFields[F.TABLE_INFO].ContainerFields[0][F.TABLE_ID] = {
                    ...freshFields[F.TABLE_INFO].ContainerFields[0][F.TABLE_ID],
                    FieldValue: table.tableId,
                };
                // ── Table Information container entry for the lock — pre-set to
                // the same RESSTATUS row (ActionId 6 TABLE, StatusId 61 TABLE
                // LOCKED) the deferred request will eventually carry, so the
                // container data already agrees with the lock even though the
                // request row itself isn't inserted until the first real save. ──
                const tableLockedStatus = this._resolveStatus(6, 61);
                freshFields[F.TABLE_INFO].ContainerFields[0][F.TABLE_STATUS] = {
                    ...freshFields[F.TABLE_INFO].ContainerFields[0][F.TABLE_STATUS],
                    FieldValue: tableLockedStatus ? tableLockedStatus.StatusId : null,
                };
            }
            return {
                selectedTable: table,
                view: 'order',
                stageFields: freshFields,
                DynamicRequestID: null,
                pendingSave: null,
                tableSessions: {
                    ...prev.tableSessions,
                    [table.tableId]: {
                        startTime: Date.now(),
                        stageFields: freshFields,
                        DynamicRequestID: null,
                        // Not 'running' yet — nothing's been saved. The grid badge
                        // reads this status directly, so marking it 'running' here
                        // showed "Occupied" the moment a table was opened, before an
                        // order existed. Flips to 'running' once saveRequest's first
                        // real save lands (see the isInitialEntry branch below),
                        // matching the backend's Locked → Occupied flip.
                        status: 'locked',
                    },
                },
            };
        }, () => this.updateTableMasterStatus(table, TABLE_STATUS_LOCKED));
    };


    // markAvailable defaults true for any explicit "Close Table" trigger, but
    // completePayment passes false: paying the bill closes the order, it
    // doesn't mean the table's been physically cleared yet, so freeing it back
    // to Available is left to the explicit close-table action on
    // CaptainDashboard instead of happening automatically the moment payment
    // lands.
    closeTable = (markAvailable = true) => {
        const { selectedTable, tableSessions } = this.state;
        const { activeOrderType } = this.props;

        this.props.clearCacheStorage && this.props.clearCacheStorage('tableLists', null);

        // ── Free the table back up on the Table Master now that its order is
        // closed/paid, so the next click sees it as Available again. ──────────
        if (markAvailable && selectedTable) {
            this.updateTableMasterStatus(selectedTable, TABLE_STATUS_AVAILABLE);
        }

        // ── Leave whatever /pos/order/:requestid URL this table was opened on ──
        // (handleTableSelect's running-order push, or a direct link) so a refresh
        // or back/forward doesn't re-fetch an order that was just closed/paid.
        const { pathname } = this.props.location || {};
        if (pathname && pathname !== '/pos/' && pathname !== '/pos') {
            this.props.history.push('/pos/');
        }

        this.setState(prev => {
            const updated = { ...prev.tableSessions };
            if (selectedTable) delete updated[selectedTable.tableId];

            return {
                view: activeOrderType === ORDER_TYPES.DINE_IN ? 'tables' : 'order',
                selectedTable: null,
                stageFields: buildStageFields(this.props.activeOrderType),
                DynamicRequestID: null,
                tableSessions: updated,
                // ── Cancel any in-flight save-rehydration for the table being closed ──
                // so its response (arriving after this reset) can't reopen it, and drop
                // any save queued behind it — it belongs to the table just closed.
                loadOnce: false,
                pendingRequestId: null,
                loading: false,
                pendingSave: null,
            };
        }, () => this.fetchRunningOrders());
    };

    // ── Releases a table that's stuck Locked (clicked, then abandoned before
    // an order was ever created on it) — flips the Table Master back to
    // Available. Anyone viewing the grid can do this, not just whoever locked
    // it: Locked only exists to stop two people accidentally starting an
    // order on the same table at once, not to grant one user an exclusive
    // claim on it. Only meaningful for a Locked table — an Occupied one has a
    // real order sitting on it and must be freed through closeTable/payment
    // instead, never through this. ────────────────────────────────────────
    releaseLockedTable = (table) => {
        if (!table) return;
        this.updateTableMasterStatus(table, TABLE_STATUS_AVAILABLE);
        this.setState(prev => {
            if (!prev.tableSessions[table.tableId]) return null;
            const updated = { ...prev.tableSessions };
            delete updated[table.tableId];
            return { tableSessions: updated };
        });
    };

    handleBackToTables = () => {
        const { selectedTable, stageFields, tableSessions } = this.state;

        // The table cards read their order number and amount from this list,
        // and it was last fetched before whatever was just saved on this order.
        this.fetchRunningOrders();

        // ── Leave whatever /pos/order/:requestid URL this table was opened on —
        // the order stays cached in tableSessions below, but the URL should only
        // ever name the order currently being viewed. ─────────────────────────
        const { pathname } = this.props.location || {};
        if (pathname && pathname !== '/pos/' && pathname !== '/pos') {
            this.props.history.push('/pos/');
        }

        // ── use selectedTable.tableId ─────────────────────────────────────────
        if (selectedTable && tableSessions[selectedTable.tableId]) {
            this.setState(prev => ({
                view: 'tables',
                selectedTable: null,
                stageFields: buildStageFields(),
                DynamicRequestID: null,
                tableSessions: {
                    ...prev.tableSessions,
                    [selectedTable.tableId]: {
                        ...prev.tableSessions[selectedTable.tableId],
                        stageFields,
                        DynamicRequestID: prev.DynamicRequestID,
                    },
                },
            }));
            return;
        }
        this.setState({ view: 'tables', selectedTable: null, stageFields: buildStageFields(), DynamicRequestID: null });
    };

    // MI_MODIFIER_SEL is a type-3 (MULTIDROPDOWN) field — buildFieldValue's
    // serializeFieldValue below JSON.stringifies array FieldValues for the
    // save payload, and this schema's save endpoint requires that field to
    // go over the wire as "[]"/'["id1","id2"]', not a plain string. So a
    // row's MI_MODIFIER_SEL is always kept as a real array in stageFields;
    // this normalizes it back out of whatever the backend echoes (array,
    // JSON string, or a plain comma list) when resolving display names.
    parseModifierIds = (value) => {
        if (Array.isArray(value)) return value;
        if (!value) return [];
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                if (Array.isArray(parsed)) return parsed;
            } catch (e) { /* not JSON — treat as a plain comma list */ }
            return value.split(',').map(id => id.trim()).filter(Boolean);
        }
        return [];
    };

    resolveModifiersLabel = (value) => {
        const ids = this.parseModifierIds(value);
        if (ids.length === 0) return '';
        const ModifierMaster = this.props.Modifier || [];
        return ids
            .map(id => {
                const found = ModifierMaster.find(m => m.value === id);
                return found ? found.label : id;
            })
            .join(', ');
    };

    // ── Cart operations - write directly into the Menu Items container ────
    // `item` shape from MenuList: { id, name, price, category, modifierIds,
    // modifiersLabel } — modifierIds is a real array (see parseModifierIds
    // above for why), modifiersLabel is the ready-made display string
    // ("Extra Cheese, No Onion x2") MenuList.js built from the same
    // selection.

    addToCart = (item) => {
        const modifierIds = Array.isArray(item.modifierIds) ? item.modifierIds : [];
        const modifiersLabel = item.modifiersLabel || '';
        // Comma-joined, positionally paired with modifierIds (e.g. "2,1" for
        // 2x the first modifier, 1x the second) — see MenuList's confirmAddToCart.
        const modifierQtys = item.modifierQtys || '';
        const modifierIdsKey = JSON.stringify(modifierIds);

        this.setState((prev) => {
            const rows = prev.stageFields[F.MENU_ITEMS].ContainerFields;
            // Same dish added again with a different (or no) set of add-ons
            // must land as its own row, not bump an unrelated one's qty.
            const existingIndex = rows.findIndex(r =>
                r[F.MI_ITEM].FieldValue === item.id &&
                r[F.MI_KOT_STATUS].FieldValue === 'Pending' &&
                JSON.stringify(r[F.MI_MODIFIER_SEL]?.FieldValue || []) === modifierIdsKey
            );

            let stageFields;
            if (existingIndex > -1) {
                // Number(): a row built here in the browser holds a real number, but
                // one rehydrated from the backend holds the string the wire carried,
                // and "2" + 1 concatenates to "21" instead of adding. Only reachable
                // on an order opened for edit (/pos/order/:requestid) — which is
                // exactly where re-adding an item already on the order happens.
                const qtyField = rows[existingIndex][F.MI_QTY];
                stageFields = updateContainerFieldValue(prev.stageFields, F.MENU_ITEMS, existingIndex, F.MI_QTY, (Number(qtyField.FieldValue) || 0) + 1);
            } else {
                let row = buildMenuItemRow();
                row[F.MI_ITEM] = { ...row[F.MI_ITEM], FieldValue: item.id };
                row[F.MI_RATE] = { ...row[F.MI_RATE], FieldValue: item.price };
                row[F.MI_QTY] = { ...row[F.MI_QTY], FieldValue: 1 };
                row[F.MI_KITCHEN] = { ...row[F.MI_KITCHEN], FieldValue: item.kitchen || '' };
                row[F.MI_UOM] = { ...row[F.MI_UOM], FieldValue: item.uom || '' };
                row[F.MI_MODIFIER_SEL] = { ...row[F.MI_MODIFIER_SEL], FieldValue: modifierIds };
                row.__displayName = item.name;
                row.__modifiersLabel = modifiersLabel;
                row.__modifierQtys = modifierQtys;
                // buildMenuItemRow() leaves mkField's default ChildRowNmbr of 1 on
                // every field, so without this every cart row would go up as row 1.
                row = setChildRowNmbr(row, rows);
                stageFields = addContainerRow(prev.stageFields, F.MENU_ITEMS, row);
            }
            return { stageFields, ...this._syncSession(prev, stageFields) };

        });
    };

    // TEMPORARY, alongside buildDeliveryKotSync: on an existing delivery order a
    // line already saved on the server is set to qty 0 instead of being removed.
    // Removing it left both its saved attribute row and its KOT row at the old
    // quantity (the backend updates the rows it is sent, it doesn't delete the
    // ones it isn't), and taking out the last line hid the Save button with it.
    // A line never saved (FieldId still '') has nothing on the server to update,
    // so it is still just removed.
    _keepAtZeroQty = (prev, rowIndex) => {
        const orderType = prev.stageFields[F.ORDER_TYPE]?.FieldValue || this.props.activeOrderType;
        const isDelivery = String(orderType).toLowerCase() === ORDER_TYPES.DELIVERY.toLowerCase();
        const hasSavedOrder = !!prev.DynamicRequestID && prev.DynamicRequestID !== ZERO_GUID;
        const fieldId = prev.stageFields[F.MENU_ITEMS].ContainerFields[rowIndex]?.[F.MI_ITEM]?.FieldId;
        const isSavedLine = !!fieldId && fieldId !== ZERO_GUID;
        return isDelivery && hasSavedOrder && isSavedLine;
    };

    updateQty = (rowIndex, delta) => {
        this.setState((prev) => {
            const rows = prev.stageFields[F.MENU_ITEMS].ContainerFields;
            // Same string-vs-number trap as addToCart above: on a rehydrated order
            // a bare + delta gave "2" + 1 === "21" on a plus, and "2" + -1 ===
            // "2-1" on a minus — which then failed the <= 0 test below (NaN
            // comparison) and got written straight back into the row.
            const newQty = (Number(rows[rowIndex][F.MI_QTY].FieldValue) || 0) + delta;

            let stageFields;
            if (newQty <= 0) {
                stageFields = this._keepAtZeroQty(prev, rowIndex)
                    ? updateContainerFieldValue(prev.stageFields, F.MENU_ITEMS, rowIndex, F.MI_QTY, 0)
                    : removeContainerRow(prev.stageFields, F.MENU_ITEMS, rowIndex);
            } else {
                stageFields = updateContainerFieldValue(prev.stageFields, F.MENU_ITEMS, rowIndex, F.MI_QTY, newQty);
            }
            return { stageFields, ...this._syncSession(prev, stageFields) };
        });
    };

    removeFromCart = (rowIndex) => {
        this.setState((prev) => {
            const stageFields = this._keepAtZeroQty(prev, rowIndex)
                ? updateContainerFieldValue(prev.stageFields, F.MENU_ITEMS, rowIndex, F.MI_QTY, 0)
                : removeContainerRow(prev.stageFields, F.MENU_ITEMS, rowIndex);
            return { stageFields, ...this._syncSession(prev, stageFields) };
        });
    };

    // "Send to Kitchen" = flip Pending rows to Sent + append a KOT Information row per item
    sendToKitchen = () => {
        // this.state.loading, not this.props.loading (a distinct, unrelated
        // Redux value) — a save is already in flight, so ignore this click
        // rather than queue another kitchen-send behind it.
        if (this.state.loading) return;
        this.setState((prev) => {
            const rows = prev.stageFields[F.MENU_ITEMS].ContainerFields;
            const pendingIndexes = rows
                .map((r, i) => (r[F.MI_KOT_STATUS].FieldValue === 'Pending' ? i : -1))
                .filter(i => i > -1);

            if (pendingIndexes.length === 0) return null;

            let stageFields = prev.stageFields;
            // const kotNumber = 'KOT-' + Date.now();
            const kotNumber = null

            pendingIndexes.forEach((idx) => {
                stageFields = updateContainerFieldValue(stageFields, F.MENU_ITEMS, idx, F.MI_KOT_STATUS, 'Sent');
                const menuRow = stageFields[F.MENU_ITEMS].ContainerFields[idx];
                let kotRow = buildKotInfoRow();
                kotRow[F.KOT_NUMBER] = { ...kotRow[F.KOT_NUMBER], FieldValue: kotNumber };
                kotRow[F.KOT_KITCHEN] = { ...kotRow[F.KOT_KITCHEN], FieldValue: menuRow[F.MI_KITCHEN].FieldValue };
                kotRow[F.KOT_MENU_ITEM] = { ...kotRow[F.KOT_MENU_ITEM], FieldValue: menuRow[F.MI_ITEM].FieldValue };
                kotRow = setChildRowNmbr(kotRow, stageFields[F.KOT_INFO].ContainerFields);
                stageFields = addContainerRow(stageFields, F.KOT_INFO, kotRow);
            });

            return { stageFields, ...this._syncSession(prev, stageFields) };
        }, () => this.saveRequest('submit', 'kitchen'));
        //   this.printKOT(); // uncomment if needed to automatically print KOT after sending to kitchen
    };


    // ── Billing screen — same URL, no navigation, just a view swap ────────
    // handleFinish = () => {
    //     const cartRows = this.state.stageFields[F.MENU_ITEMS].ContainerFields;
    //     if (cartRows.length === 0) {
    //         this.growl && this.growl.show({ severity: 'error', summary: null, detail: 'No items on this order to bill' });
    //         return;
    //     }

    //     // ── Update bill summary fields before switching to billing screen ─────
    //     let stageFields = this.state.stageFields;
    //     const total = this.getGrandTotal();
    //     stageFields = updateContainerFieldValue(stageFields, F.BILL_SUMMARY, 0, F.BILL_GRAND_TOTAL, total);
    //     stageFields = updateContainerFieldValue(stageFields, F.BILL_SUMMARY, 0, F.BILL_GROSS, total);

    //     this.setState({ stageFields }, () => {
    //         // ── Save with 'bill' context → ActionId 4, StatusId 41 (BILL GENERATED)
    //         // This mirrors sendToKitchen which saves with 'kitchen' context
    //         this.saveRequest('submit', 'bill');
    //         this.setState({ view: 'billing' });
    //         // this.printBill(); uncomment if needed to automatically print bill when switching to billing screen
    //     });

    // };
    handleFinish = () => {
        const cartRows = this.state.stageFields[F.MENU_ITEMS].ContainerFields;
        if (cartRows.length === 0) {
            this.growl && this.growl.show({ severity: 'error', summary: null, detail: 'No items on this order to bill' });
            return;
        }
        if (this.hasPendingCartItems()) {
            this.growl && this.growl.show({ severity: 'error', summary: null, detail: 'Send all pending items to kitchen before generating the bill' });
            return;
        }

        // ── Compute total only from Sent rows — Pending (cart) items excluded ─
        const total = this.getSentItemsTotal();

        const billRows = this.state.stageFields[F.BILL_SUMMARY]?.ContainerFields || [];
        const billRowIndex = Math.max(0, billRows.findIndex(row => !isTemplateRow(row)));

        let stageFields = this.state.stageFields;
        stageFields = updateContainerFieldValue(stageFields, F.BILL_SUMMARY, billRowIndex, F.BILL_GRAND_TOTAL, total);
        stageFields = updateContainerFieldValue(stageFields, F.BILL_SUMMARY, billRowIndex, F.BILL_GROSS, total);

        // The bill screen opens from the save's own success callback. Switching
        // straight away showed a "generated" bill even when the save was still
        // queued behind another one or failed, with no BILL row ever written.
        this.setState({ stageFields }, () => {
            this.saveRequest('submit', 'bill', () => this.setState({ view: 'billing' }));
        });
    };


    backToOrderFromBilling = () => this.setState({ view: 'order' });

    // completePayment = (bill, paymentEntries, changeReturn) => {
    //     this.setState({ billingSubmitting: true });

    //     let stageFields = this.state.stageFields;

    //     // Bill summary
    //     stageFields = updateContainerFieldValue(stageFields, F.BILL_SUMMARY, 0, F.BILL_GROSS, bill.grossAmount);
    //     stageFields = updateContainerFieldValue(stageFields, F.BILL_SUMMARY, 0, F.BILL_ITEM_DISCOUNT, bill.itemDiscount);
    //     stageFields = updateContainerFieldValue(stageFields, F.BILL_SUMMARY, 0, F.BILL_TAX, bill.tax);
    //     stageFields = updateContainerFieldValue(stageFields, F.BILL_SUMMARY, 0, F.BILL_ROUND_OFF, bill.roundOff);
    //     stageFields = updateContainerFieldValue(stageFields, F.BILL_SUMMARY, 0, F.BILL_GRAND_TOTAL, bill.grandTotal);

    //     // Payment info
    //     stageFields = updateContainerFieldValue(stageFields, F.PAYMENT_INFO, 0, F.PAY_CHANGE_RETURN, changeReturn);
    //     stageFields = updateContainerFieldValue(stageFields, F.PAYMENT_INFO, 0, F.PAY_STATUS, 'Paid');

    //     // Order status closed
    //     stageFields = {
    //         ...stageFields,
    //         [F.ORDER_STATUS]: {
    //             ...stageFields[F.ORDER_STATUS],
    //             FieldValue: 'Closed',
    //         },
    //     };

    //     this.setState({ stageFields, paymentEntries }, () => {
    //         // 'payment' context sends:
    //         //   BILL rows   → ActionId 4, StatusId 43 (PAID)
    //         //   PAYMENT rows → ActionId 5, StatusId 53 (PAID)
    //         this.saveRequest('submit', 'payment');
    //         this.setState({ billingSubmitting: false });
    //         this.closeTable();
    //     });
    // };

    completePayment = (bill, changeReturn) => {
        this.setState({ billingSubmitting: true });

        let stageFields = this.state.stageFields;

        // ── Bill summary ──────────────────────────────────────────────────────
        stageFields = updateContainerFieldValue(stageFields, F.BILL_SUMMARY, 0, F.BILL_GROSS, bill.grossAmount);
        stageFields = updateContainerFieldValue(stageFields, F.BILL_SUMMARY, 0, F.BILL_ITEM_DISCOUNT, bill.itemDiscount);
        stageFields = updateContainerFieldValue(stageFields, F.BILL_SUMMARY, 0, F.BILL_TAX, bill.tax);
        stageFields = updateContainerFieldValue(stageFields, F.BILL_SUMMARY, 0, F.BILL_ROUND_OFF, bill.roundOff);
        stageFields = updateContainerFieldValue(stageFields, F.BILL_SUMMARY, 0, F.BILL_GRAND_TOTAL, bill.grandTotal);

        // ── Loop all payment rows — mark each Paid, change return on last only ─
        const payRows = stageFields[F.PAYMENT_INFO].ContainerFields;
        const realRows = payRows.filter(
            row => Number(row[F.PAY_AMOUNT_TENDERED]?.FieldValue || 0) > 0
        );

        realRows.forEach((_, idx) => {
            const isLast = idx === realRows.length - 1;
            stageFields = updateContainerFieldValue(
                stageFields, F.PAYMENT_INFO, idx, F.PAY_STATUS, 'Paid'
            );
            stageFields = updateContainerFieldValue(
                stageFields, F.PAYMENT_INFO, idx, F.PAY_CHANGE_RETURN,
                isLast ? changeReturn : 0
            );
        });

        // ── Order status closed ───────────────────────────────────────────────
        stageFields = {
            ...stageFields,
            [F.ORDER_STATUS]: {
                ...stageFields[F.ORDER_STATUS],
                FieldValue: 'Closed',
            },
        };

        this.setState({ stageFields }, () => {
            // false: don't mark the table Available here — see closeTable's
            // markAvailable param. Freeing it is CaptainDashboard's explicit
            // close-table action, not an automatic side effect of payment.
            this.saveRequest('submit', 'payment', () => this.closeTable(false));
            this.setState({ billingSubmitting: false });

        });
    };
    addPayment = (payment) => {

        this.setState(prev => {

            const row = buildPaymentRow();

            row[F.PAY_TYPE].FieldValue = payment.paymentType;

            row[F.PAY_AMOUNT_TENDERED].FieldValue = payment.amount;

            row[F.PAY_TRANSACTION_NO].FieldValue = payment.transactionNo || '';

            row[F.PAY_APPROVAL_NO].FieldValue = payment.approvalNo || '';

            row[F.PAY_CARD_NO].FieldValue = payment.cardNo || '';

            row[F.PAY_BANK].FieldValue = payment.bank || '';

            row[F.PAY_CHANGE_RETURN].FieldValue = payment.changeReturn || 0;

            row[F.PAY_STATUS].FieldValue = payment.status || 'Paid';

            const stageFields = addContainerRow(
                prev.stageFields,
                F.PAYMENT_INFO,
                setChildRowNmbr(row, prev.stageFields[F.PAYMENT_INFO].ContainerFields)
            );

            return {
                stageFields,
                ...this._syncSession(prev, stageFields)
            };

        });

    };

    // Launches Razorpay Checkout for `amount` (whatever the cashier typed
    // into BillingScreen's Enter Amount box — not necessarily the full
    // outstanding balance, so this can be one leg of a Cash + Razorpay split)
    // and, on a verified success, records it via the same addPayment() the
    // manual Cash/Paytm/etc "Add" button uses. Returns a Promise (settles
    // either way) so BillingScreen can clear its own "processing" state
    // regardless of outcome.
    payWithRazorpay = (amount, paymentTypeValue) => {
        const receipt = this.state.stageFields[F.ORDER_NUMBER]?.FieldValue || `pos-${Date.now()}`;

        // Test API (c:\work\Razorpay\Razorpay\Program.cs) takes the amount in
        // rupees and converts to paise itself — do NOT *100 here. It also
        // generates its own `receipt` server-side and ignores any sent here;
        // `receipt` is only used locally for the Checkout description below.
        return razorpayAxios.post('/create-order', {
            amount,
            currency: 'INR',
            // Tags the Razorpay order with this bill, so BillingScreen can find
            // the payment again (GET /bill-payments) if the page reloads before
            // /verify-payment returns. The backend has to forward these as the
            // order's `notes`.
            notes: {
                orderNumber: this.state.stageFields[F.ORDER_NUMBER]?.FieldValue || '',
                requestId:   this.state.DynamicRequestID && this.state.DynamicRequestID !== ZERO_GUID
                    ? this.state.DynamicRequestID
                    : '',
            },
        }).catch(err => {
            // Only the create-order call gets this generic toast — failures
            // further down (verification, cancel, payment.failed) already
            // show their own specific message inside the Promise below.
            this.growl && this.growl.show({ severity: 'error', summary: null, detail: 'Could not start Razorpay payment. Please try again.' });
            throw err;
        }).then(({ data }) => new Promise((resolve, reject) => {
            // /create-order currently forwards Razorpay's raw order object
            // with no key_id (Checkout needs one client-side to open) — this
            // reads either a { order, key_id } wrapper or a flat order with
            // key_id merged in, whichever shape the backend ends up using.
            // TODO: simplify once that response shape is confirmed.
            const order = data.order || data;
            const keyId = data.key_id || order.key_id;

            openRazorpayCheckout({
                order,
                keyId,
                name: 'POS Order',
                description: `Order ${receipt}`,
                onSuccess: (response) => {
                    razorpayAxios.post('/verify-payment', {
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_signature: response.razorpay_signature,
                    }).then(({ data: verification }) => {
                        if (verification && verification.verified) {
                            this.addPayment({
                                paymentType: paymentTypeValue,
                                amount,
                                transactionNo: response.razorpay_payment_id,
                                status: 'Paid',
                            });
                            resolve();
                        } else {
                            this.growl && this.growl.show({ severity: 'error', summary: null, detail: 'Payment verification failed' });
                            reject(new Error('verification failed'));
                        }
                    }).catch(() => {
                        this.growl && this.growl.show({ severity: 'error', summary: null, detail: 'Payment verification failed' });
                        reject(new Error('verification request failed'));
                    });
                },
                onFailure: (err) => {
                    if (err && err.message !== 'Payment cancelled') {
                        this.growl && this.growl.show({ severity: 'error', summary: null, detail: 'Razorpay payment failed' });
                    }
                    reject(err);
                },
            });
        }));
    };


    _syncSession = (prev, stageFields) => {
        // ── use selectedTable.tableId ─────────────────────────────────────────
        if (!prev.selectedTable || !prev.tableSessions[prev.selectedTable.tableId]) return {};
        return {
            tableSessions: {
                ...prev.tableSessions,
                [prev.selectedTable.tableId]: {
                    ...prev.tableSessions[prev.selectedTable.tableId],
                    stageFields,
                    DynamicRequestID: prev.DynamicRequestID,
                },
            },
        };
    };


    // ── Generic setter for flat (non-container) top-level fields ──────────
    updateField = (fieldKey, value) => {
        this.setState(prev => ({
            stageFields: {
                ...prev.stageFields,
                [fieldKey]: { ...prev.stageFields[fieldKey], FieldValue: value },
            },
        }));
    };

    // ── Generic setter for a single-row container's child field ──────────
    updateContainerField = (containerKey, childKey, value, rowIndex = 0) => {
        this.setState(prev => ({
            stageFields: updateContainerFieldValue(prev.stageFields, containerKey, rowIndex, childKey, value),
        }));
    };


    getGrandTotal = () => {
        const rows = this.state.stageFields[F.MENU_ITEMS].ContainerFields;
        return rows.reduce((sum, r) => {
            const qty = Number(r[F.MI_QTY].FieldValue) || 0;
            const rate = Number(r[F.MI_RATE].FieldValue) || 0;
            return sum + (qty * rate);
        }, 0);
    };

    printKOT = () => {
        console.log('Preparing KOT print data with stageFields:', this.state.stageFields);
        const data = buildKotPrintData(this.state.stageFields, {
            menuItemsList: this.props.menuitems,
            kitchenList: this.props.Kitchen,
        });
        this.setState({ printModal: { type: 'kot', data } });
    };

    printBill = () => {
        console.log('Preparing Bill print data with stageFields:', this.state.stageFields);
        const data = buildBillPrintData(this.state.stageFields, {
            menuItemsList: this.props.menuitems,
            paymentTypeList: this.props.PaymentType,
        });
        this.setState({ printModal: { type: 'bill', data } });
    };

    closePrintModal = () => this.setState({ printModal: null });

    // Print, on the BILL preview only. Never opens the browser print dialog.
    //
    // Generating the bill already sets printingrequired (see the 'bill' branch
    // of buildRequestActions), so by the time this preview is open the bill is
    // usually queued and may already be on paper. This button is therefore a
    // safety net: it re-sets the flag in case that save did not carry it.
    //
    // Re-setting is harmless. The enqueuer's NOT EXISTS check sees the job
    // that already exists for this order and creates nothing, so clicking
    // Print twice cannot produce two bills.
    //
    // KOTs are deliberately untouched - they already queue on Send to Kitchen.
    queueBillPrint = async () => {
        const billRows = (this.state.requestActionsRaw || []).filter(r =>
            Number(pick(r, 'actiontype', 'actionType', 'ActionType')) === 4
        );

        if (billRows.length === 0) {
            // requestActionsRaw comes from the order GET, so it can be stale
            // in the same session the bill was generated. Nothing to re-flag
            // here, and the generate step has already queued it - so close
            // quietly rather than claiming something is wrong.
            this.closePrintModal();
            return;
        }

        try {
            const payload = billRows.map(r => ({ ...r, printingrequired: PRINT_FLAG_ON }));
            await axios.post(
                '/master/api/UpdateDynamicData?model=Restaurant_Action_Update',
                { Key: JSON.stringify(payload) }
            );

            this.growl && this.growl.show({
                severity: 'success', summary: 'Sent to printer',
                detail: 'The bill has been queued.',
            });
            this.closePrintModal();
        } catch (err) {
            // Left open so the user can retry without rebuilding the preview.
            this.growl && this.growl.show({
                severity: 'error', summary: 'Could not queue the bill',
                detail: err?.response?.data?.message || err?.message || 'Please try again.',
            });
        }
    };


    //Beta under testing 

    // ─────────────────────────────────────────────────────────────────────────────
    // HELPER — look up a single RESSTATUS row by ActionId + StatusId.
    // Used by action branches to resolve the correct master record before
    // building a payload row, so ActionId/StatusId always match RESSTATUS exactly.
    // ─────────────────────────────────────────────────────────────────────────────
    // The live action/status flow (GetresActionStatusflow). Its Id/StatusId/
    // Sequence go straight into every action row, so they have to match the
    // backend's RESSTATUS master, which has been renumbered since
    // the hardcoded list it replaced was written. A direct axios call, not
    // getMasterData: that caches masters in localStorage indefinitely. Same
    // fetch and mapping as SpecialOrderDetails.fetchResStatusMaster.
    fetchResStatusMaster = () => {
        axios.get(
            '/master/api/res/GetresActionStatusflow?companyId=' + this.props.loggedUserDetail?.CompanyId
        )
            .then((response) => {
                const rows = Array.isArray(response.data) ? response.data : [];
                this.setState({
                    resStatusMaster: rows
                        .filter((r) => r.isActive !== false)
                        .map((r) => ({
                            Id: r.id,
                            ActionId: Number(r.actionid),
                            ActionName: (r.actionname || '').trim(),
                            StatusId: Number(r.statusid),
                            StatusName: (r.statusName || '').trim(),
                            Sequence: r.sequence,
                        })),
                });
            })
            .catch(() => this.setState({ resStatusMaster: [] }));
    };

    // The row for this action/status, matched by number, from the live list
    // only; null until it has loaded.
    _resolveStatus = (actionId, statusId) => (
        (this.state.resStatusMaster || []).find(
            r => r.ActionId === actionId && r.StatusId === statusId
        ) || null
    );

    // Same lookup for a row about to be saved: a status missing from the live
    // list (or the list not loaded yet) stops the save with a message instead
    // of writing a row with no status.
    _requireStatus = (actionId, statusId) => {
        const row = this._resolveStatus(actionId, statusId);
        if (row) return row;
        if (!this.state.resStatusMaster || this.state.resStatusMaster.length === 0) {
            this.fetchResStatusMaster();
            throw new Error('Status list is still loading. Please try again.');
        }
        throw new Error(`Status ${statusId} (action ${actionId}) is missing from the status master.`);
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // BASE ROW BUILDER — maps 1:1 to every column in REQUESTACTIONS.
    // ActionId and StatusId come directly from RESSTATUS so they are always
    // consistent with the master table.  Every branch spreads this and overrides
    // only the columns relevant to that action type.
    // ─────────────────────────────────────────────────────────────────────────────
    _buildBaseActionRow = (actionNum, resStatusRow, activeStatusId, targetDate) => ({
        // ── Core / workflow ───────────────────────────────────────────────────
        Actionnum: actionNum,
        // RESSTATUS reference — both columns must be sent together
        ActionId: resStatusRow.ActionId,          // → ACTIONID   (FK to RESSTATUS.ACTIONID)
        actiontype: resStatusRow.ActionId,          // → ACTIONID   (FK to RESSTATUS.ACTIONID)
        ActionName: resStatusRow.ActionName,        // → ACTIONNAME
        ResStatusId: resStatusRow.Id,                // → RESSTATUS.ID (the PK row reference)
        StatusId: activeStatusId,                 // → STATUS_ID  (null on seed, real value on update)
        StatusName: resStatusRow.StatusName,        // → STATUS_NAME
        Sequence: resStatusRow.Sequence,          // → SEQUENCE
        ActionDescription: resStatusRow.ActionName,        // → ACTIONDESCRIPTION
        ActionCode: resStatusRow.ActionName,        // → actioncode
        Targetdate: moment(targetDate).toISOString(),  // → TARGETDATE
        // Dine-in/QR orders have no separate delivery date input — stamped with
        // the action's own date (today) so the Captain Dashboard can filter
        // KOTs by delivery date the same way as special/delivery orders.
        DeliveryDate: moment(targetDate).format('MM/DD/YYYY'),  // → DELIVERYDATE
        companyId: this.props.loggedUserDetail.CompanyId,  // → COMPANYID

        // ── Print flags ───────────────────────────────────────────────────────
        // Off by default, so a row only ever prints when something deliberately
        // turns printingrequired on. isprinted is never set to anything else
        // here — the print service owns it, and writing '0' over a '1' would
        // reprint a ticket that has already come out.
        printingrequired: PRINT_FLAG_OFF,
        isprinted: PRINT_FLAG_OFF,

        // Workflow assignment (placeholder — wire when needed)
        AssignedTo: null,                           // → ASSIGNED_TO
        AssignedByEmployeeId: null,                           // → ASSIGNED_BY_EMPLOYEE_ID
        DeligateToEmployeeId: null,                           // → DELIGATE_TO_EMPLOYEE_ID
        DelicateToEmpEmailId: null,                           // → DELICATE_TO_EMP_EMAIL_ID
        DeligateFromDate: null,                           // → DELIGATE_FROM_DATE
        DeligateToDate: null,                           // → DELIGATE_TO_DATE
        Comments: null,                           // → COMMENTS

        // ── Order header ──────────────────────────────────────────────────────
        OrderType: null,   // → ordertype
        Branch: null,   // → branch
        Waiter: null,   // → waiter
        Cashier: null,   // → cashier
        Shift: null,   // → shift
        TokenNumber: null,   // → tokennumber
        BillNumber: null,   // → billnumber

        // ── Table ─────────────────────────────────────────────────────────────
        TableNumber: null,   // → tablenumber
        TableId: null,   // → tableid
        TableSubCode: null,   // → tablesubcode
        // NumberOfGuest:              null,   // → numberofguest
        AdultCount: null,   // → adultcount
        ChildCount: null,   // → childcount
        ReservationNumber: null,   // → reservationnumber
        TableStatus: null,   // → tablestatus

        // ── Customer ──────────────────────────────────────────────────────────
        CustomerName: null,   // → customername
        CustomerMobileNumber: null,   // → customermobilnumber
        CustomerMembershipNo: null,   // → customermembershipno
        CustomerGstNo: null,   // → customergstno
        CustomerRemarks: null,   // → customerremarks

        // ── Delivery ──────────────────────────────────────────────────────────
        DeliveryAddress: null,   // → deliveryaddress
        DeliveryLandmark: null,   // → deliverylandmark
        DeliveryGoogleLocation: null,   // → deliverygooglelocation
        DeliveryBuilding: null,   // → deliverybuilding
        DeliveryFloor: null,   // → deiveryfloor        (DB typo kept)
        DeliveryDoor: null,   // → deliverydoor
        DeliveryBoy: null,   // → DeliveryBoy
        DeliveryCharge: null,   // → DeliveryCharrge     (DB typo kept)
        ExpectedDeliveryTime: null,   // → ExpectedDeliveryTime
        Distance: null,   // → Distance
        DeliveryStatus: null,   // → DeliveryStatus
        DeliveryInstruction: null,   // → DeliveryInstruction

        // ── Menu item / KOT ───────────────────────────────────────────────────
        MenuId: null,   // → MenuId
        KitchenId: null,   // → kitchenid
        KOTNumber: null,   // → KOTNumber
        // NEW/OLD per item — set per-row in the 'kitchen' action builder via
        // isNewMenuItem. Defaults to null for every other action context
        // (bill, payment, delivery, table lock), where it doesn't apply.
        pqejrer: null,
        KOTInstruction: null,   // → KOTInstruction
        ModifierId: null,   // → ModifierId
        MenuQty: null,   // → MenuQty
        ModifierQty: null,   // → ModifierQty
        MenuUOM: null,   // → MenuUOM
        ModifierUOM: null,   // → ModifierUOM
        MenuItemRate: null,   // → menuitemrate
        MenuItemDiscountPercentage: null,   // → menuitemdiscountpercentage
        MenuItemTaxPercentage: null,   // → menuitemtaxpercentage
        MenuItemNetAmount: null,   // → menuitemnetamount
        MenuItemKitchenId: null,   // → menuitemkitchenid
        MenuItemSpecialInstruction: null,   // → menuitemspecialinstruction
        MenuItemKotStatus: null,   // → menuitemkotstaus    (DB typo kept)
        MenuItemServedQty: null,   // → menuitemservedqty
        MenuItemPreparedQty: null,   // → menuitempreparedqty
        MenuItemBalanceQty: null,   // → menuitembalanceqty
        MenuItemCancelledQty: null,   // → menuitemcancelledqty

        // ── Bill header ───────────────────────────────────────────────────────
        BillHeaderGrossAmount: null,   // → billheadergrossamount
        BillHeaderItemDiscount: null,   // → billheaderitemdiscount
        BillHeaderBillDiscount: null,   // → billheadbilldiscount
        BillHeaderTax: null,   // → billheadtax
        BillHeaderServiceCharge: null,   // → billheadservicecharge
        BillHeaderPackingCharge: null,   // → billheadpackingcharge
        BillHeaderDeliveryCharge: null,   // → billheaddeliverycharge
        BillHeaderRoundOff: null,   // → billheadroundoff
        BillHeaderGrandTotal: null,   // → billheadgrandtotal

        // ── Payment ───────────────────────────────────────────────────────────
        PaymentType: null,   // → paymenttype
        PaymentAmountTendered: null,   // → paymentamounttemdered  (DB typo kept)
        PaymentChangeReturn: null,   // → paymentchangereturn
        PaymentTransactionNumber: null,   // → paymenttransactionnumber
        PaymentCardNumber: null,   // → paymentcardnumber
        PaymentBank: null,   // → paymentbank
        PaymentApprovalNo: null,   // → paymentapprovalno
        PaymentStatus: null,   // → paymentstatus

        request: null,   // → requestid
    });

    // ─────────────────────────────────────────────────────────────────────────────
    // Single source of truth for the branch/outlet id sent to the backend, used by
    // both the action-row order header and the per-attribute `branch` key so the two
    // can never drift. Prefers the outlet explicitly picked in the POS topbar
    // (state.master.selectedBranch, which defaults to null), then the user's default
    // branch, then the long-standing fallback constant.
    // ─────────────────────────────────────────────────────────────────────────────
    _resolveBranchId = () =>
        this.props.selectedBranch
        || (this.props.loggedUserDetail && this.props.loggedUserDetail.defaultbranch)
        || '00000000-0000-0000-0000-000000000001';

    // ─────────────────────────────────────────────────────────────────────────────
    // SHARED ORDER HEADER — filled on every row regardless of action type.
    // Maps stageFields container fields → REQUESTACTIONS order header columns.
    // ─────────────────────────────────────────────────────────────────────────────
    _buildOrderHeaderFields = (stageFields) => {
        // ── Waiter/Cashier/Shift/TokenNumber/OrderNumber are top-level stageFields
        // entries, not nested in a container — there is no ORDER_INFO container in
        // RestaurantOrderFields (F). Reading them off a non-existent orderInfo
        // container previously resolved to undefined and silently sent null for
        // every one of these on every Requestactions call (Kitchen, Delivery,
        // Bill, Payment, Reject, Return alike). Same story for the customer-info
        // and table-info keys below: F.CUSTOMER_NAME/CUSTOMER_MOBILE/etc and
        // F.TABLE_ID/RESERVATION_NUMBER never existed in F — the real constants
        // are F.CUST_NAME/CUST_MOBILE/... and F.RESERVATION_NO.
        const tableInfo = stageFields[F.TABLE_INFO]?.ContainerFields?.[0];
        const customerInfo = stageFields[F.CUSTOMER_INFO]?.ContainerFields?.[0];
        return {
            OrderType: this.props.activeOrderType || null,
            Branch: this._resolveBranchId(),
            Waiter: stageFields[F.WAITER]?.FieldValue || null,
            Cashier: stageFields[F.CASHIER]?.FieldValue || null,
            Shift: stageFields[F.SHIFT]?.FieldValue || null,
            TokenNumber: stageFields[F.TOKEN_NUMBER]?.FieldValue || null,
            BillNumber: stageFields[F.ORDER_NUMBER]?.FieldValue || null,
            TableNumber: tableInfo?.[F.TABLE_NUMBER]?.FieldValue || null,
            // No dedicated table-id field in the schema — this is the JS-level id
            // selectedTable/tableSessions key off (see handleTableSelect).
            TableId: this.state.selectedTable?.tableId || null,
            TableSubCode: tableInfo?.[F.TABLE_SUB_CODE]?.FieldValue || null,
            AdultCount: tableInfo?.[F.ADULT_COUNT]?.FieldValue ?? null,
            ChildCount: tableInfo?.[F.CHILD_COUNT]?.FieldValue ?? null,
            ReservationNumber: tableInfo?.[F.RESERVATION_NO]?.FieldValue || null,
            TableStatus: tableInfo?.[F.TABLE_STATUS]?.FieldValue || null,
            CustomerName: customerInfo?.[F.CUST_NAME]?.FieldValue || null,
            CustomerMobileNumber: customerInfo?.[F.CUST_MOBILE]?.FieldValue || null,
            // The backend column is customermobilnumber ("mobil", no "e");
            // CustomerMobileNumber alone never reaches it, so the number is
            // sent under the column's own spelling too.
            CustomerMobilNumber: customerInfo?.[F.CUST_MOBILE]?.FieldValue || null,
            CustomerMembershipNo: customerInfo?.[F.CUST_MEMBERSHIP_NO]?.FieldValue || null,
            CustomerGstNo: customerInfo?.[F.CUST_GST]?.FieldValue || null,
            CustomerRemarks: customerInfo?.[F.CUST_REMARKS]?.FieldValue || null,
        };
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // SHARED BILL HEADER — reused by both 'bill' and 'payment' contexts.
    // ─────────────────────────────────────────────────────────────────────────────
    _buildBillHeaderFields = (stageFields) => {
        // handleFinish writes the totals to the first NON-template row, not to
        // row 0. Reading row 0 unconditionally meant that on an order whose
        // summary container still carries a template row the totals were read
        // back from the wrong row and went to the backend empty.
        const billRows = stageFields[F.BILL_SUMMARY]?.ContainerFields || [];
        const b = billRows[Math.max(0, billRows.findIndex(row => !isTemplateRow(row)))];
        if (!b) return {};

        const num = (field) => Number(b[field]?.FieldValue) || 0;

        // billheadgrandtotal (decimal 18,2 on requestactions) arrived as 0 on
        // every bill row written so far, so the printed bill showed Rs.0.00.
        // It is computed here rather than trusted to survive the container
        // round-trip: gross less any discount.
        //
        // Tax is NOT added on top - GST is inclusive in these prices, which is
        // why the printed bill shows the same figure as the taxable value and
        // the CGST/SGST split beneath it is a breakdown, not an addition.
        //
        // Gross falls back to the sent-items total (the same source handleFinish
        // uses) so a missing staged value cannot produce a zero bill.
        const gross = num(F.BILL_GROSS) || this.getSentItemsTotal();
        const itemDiscount = num(F.BILL_ITEM_DISCOUNT);
        const billDiscount = num(F.BILL_DISCOUNT);

        // Rounded to 2dp to match the column: floating-point subtraction of
        // two 2dp figures can land on 243.60000000000002, which SQL would
        // truncate silently and the printed bill would disagree with.
        const grandTotal = Math.max(0, Math.round((gross - itemDiscount - billDiscount) * 100) / 100);

        return {
            BillHeaderGrossAmount: gross,
            BillHeaderItemDiscount: b[F.BILL_ITEM_DISCOUNT]?.FieldValue ?? null,
            BillHeaderBillDiscount: b[F.BILL_DISCOUNT]?.FieldValue ?? null,
            BillHeaderTax: b[F.BILL_TAX]?.FieldValue ?? null,
            BillHeaderServiceCharge: b[F.BILL_SERVICE_CHARGE]?.FieldValue ?? null,
            BillHeaderPackingCharge: b[F.BILL_PACKING_CHARGE]?.FieldValue ?? null,
            BillHeaderDeliveryCharge: b[F.BILL_DELIVERY_CHARGE]?.FieldValue ?? null,
            BillHeaderRoundOff: b[F.BILL_ROUND_OFF]?.FieldValue ?? null,
            BillHeaderGrandTotal: grandTotal,
        };
    };


    // ── TEMPORARY: a delivery order's Save keeps its KOT rows in step ────────
    // A plain Save sends no action rows, so on a delivery order an edited
    // quantity or price only reached the attribute data while the KOT rows the
    // Captain Dashboard and kitchen read kept the old values. For a Save of an
    // existing delivery order this returns:
    //   - every existing KOT row for a line still in the cart: the backend's own row,
    //     requestactionid and status kept, with only menuQty / menuitemrate /
    //     menuitemnetamount set;
    //   - a new KOT row at CONFIRMED BY CUSTOMER for each cart line with no KOT
    //     row yet;
    //   - every KOT row whose line is no longer in the cart, at qty 0 (net 0);
    //   - the order's DELIVERY row, when it has one, with the current schedule.
    // Every row sent also carries the form's delivery date and time when the
    // order has them, and updated KOT rows get their modifiers back from the
    // cart line. Every other column is left alone.
    buildDeliveryKotSync = (stageFields) => {
        const lower = (v) => String(v || '').toLowerCase();
        const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

        // requestActionsRaw is the GET's data.requestactions (lowercase keys).
        // Cancelled rows are not reused for a line that is back in the cart.
        const kotRows = (this.state.requestActionsRaw || []).filter(r =>
            Number(pick(r, 'actiontype', 'actionType', 'ActionType')) === 1 &&
            Number(pick(r, 'statusId', 'statusid', 'StatusId')) !== 17 &&
            pick(r, 'menuId', 'MenuId', 'menuid')
        );

        // The server's rows carry no modifier id, so the same dish twice in one
        // kitchen is matched by position: the Nth cart line of that dish pairs
        // with the Nth KOT row for it.
        const keyOf = (menuId, kitchenId) => lower(menuId) + '|' + lower(kitchenId);
        const unmatched = {};
        kotRows.forEach(r => {
            const key = keyOf(pick(r, 'menuId', 'MenuId', 'menuid'), pick(r, 'kitchenid', 'kitchenId', 'KitchenId'));
            (unmatched[key] = unmatched[key] || []).push(r);
        });

        const menuRows = (stageFields[F.MENU_ITEMS]?.ContainerFields || [])
            .filter(row => row[F.MI_ITEM]?.FieldValue);
        const orderHeader = this._buildOrderHeaderFields(stageFields);
        const now = new Date();
        const deliveryDate = toValidDate(stageFields[F.DELIVERY_DATE]?.FieldValue) || now;

        // Delivery date and time as the KOT rows store them. The backend saves an
        // updated row exactly as it is sent, and the order-detail rows these
        // updates start from (requestActionsRaw) carry no deliverydate column at
        // all, so leaving it out saved it as null: ROD-053's and ROD-056's KOT rows
        // lost their delivery date that way, and the Captain Dashboard files KOT
        // rows by it. Only values really present on the form are written; a blank
        // field never stamps "now" over a row.
        const formDate = stageFields[F.DELIVERY_DATE]?.FieldValue;
        const formDay = formDate && moment(formDate).isValid()
            ? moment(formDate).format('MM/DD/YYYY')
            : null;
        const formTime = stageFields[F.EXPECTED_DELIVERY_TIME]?.FieldValue || null;
        const scheduleFields = {
            ...(formDay ? { deliverydate: formDay } : {}),
            ...(formTime ? { expectedDeliveryTime: formTime } : {}),
        };

        const updates = [];
        const inserts = [];

        // New rows continue after the highest actionnum already on this order.
        // A row sent without a requestactionid lands on whichever row holds its
        // actionnum: numbering every insert from 1 made each dish added on a
        // later save replace the one added before it. Counted across every
        // action type, so a new KOT row can't take the ORDER or DELIVERY row's
        // number either.
        let nextActionNum = (this.state.requestActionsRaw || []).reduce(
            (max, r) => Math.max(max, Number(pick(r, 'actionnum', 'actionNum', 'Actionnum')) || 0),
            0
        ) + 1;

        menuRows.forEach(row => {
            const qty = num(row[F.MI_QTY]?.FieldValue);
            const rate = num(row[F.MI_RATE]?.FieldValue);
            // Net amount follows qty x rate, to 2 decimals — the same way the
            // existing rows hold it (e.g. 5 x 69 = 345).
            const netAmount = qty === null || rate === null
                ? null
                : Math.round(qty * rate * 100) / 100;
            const queue = unmatched[keyOf(row[F.MI_ITEM].FieldValue, row[F.MI_KITCHEN]?.FieldValue)];
            const existing = queue && queue.length ? queue.shift() : null;

            if (existing) {
                // Sent whether or not it changed, so the payload always carries
                // one KOT row per cart line. It goes back under its own
                // requestactionid, which the backend updates in place.
                // modifierId is the other column missing from the order-detail
                // rows, so it is rebuilt from the cart line for the same reason as
                // deliverydate above; the same comma-joined form the KOT builders send.
                const modifierIds = this.parseModifierIds(row[F.MI_MODIFIER_SEL]?.FieldValue);
                updates.push({
                    ...existing,
                    menuQty: qty,
                    menuitemrate: rate,
                    menuitemnetamount: netAmount,
                    modifierId: modifierIds.length ? modifierIds.join(',') : null,
                    ...scheduleFields,
                });
                return;
            }

            // A line at qty 0 that never had a KOT row has nothing to send.
            if (!qty) return;

            inserts.push({
                ...this._buildBaseActionRow(nextActionNum++, this._requireStatus(1, 15), 15, now) /* KOT CONFIRMED BY CUSTOMER */,
                ...orderHeader,
                // The customer's delivery date, not today: the Captain Dashboard
                // files KOT rows by it.
                DeliveryDate: moment(deliveryDate).format('MM/DD/YYYY'),
                ExpectedDeliveryTime: formTime,
                pqejrer: 'NEW',
                MenuId: row[F.MI_ITEM].FieldValue,
                KitchenId: row[F.MI_KITCHEN]?.FieldValue || null,
                MenuItemKitchenId: row[F.MI_KITCHEN]?.FieldValue || null,
                MenuQty: qty,
                MenuUOM: row[F.MI_UOM]?.FieldValue || null,
                MenuItemRate: rate,
                MenuItemNetAmount: netAmount,
                // F.MI_SPECIAL_INSTR is the real key; F.MI_SPECIAL_INSTRUCTION (used by
                // the older builders below) is not defined in RestaurantOrderFields.
                MenuItemSpecialInstruction: row[F.MI_SPECIAL_INSTR]?.FieldValue || null,
                KOTInstruction: row[F.MI_SPECIAL_INSTR]?.FieldValue || null,
            });
        });

        // Rows still unpaired belong to lines no longer in the cart. A saved line
        // brought down to qty 0 now stays in the cart at 0 (_keepAtZeroQty) and
        // pairs above; this still covers any line that did get removed, so its
        // KOT row doesn't keep the old quantity. It goes back at qty 0, same id
        // and status.
        Object.keys(unmatched).forEach(key => {
            unmatched[key].forEach(existing => {
                updates.push({ ...existing, menuQty: 0, menuitemnetamount: 0, ...scheduleFields });
            });
        });

        // The order-level DELIVERY row (actiontype 3) records the same schedule,
        // its expectedDeliveryTime as one "MM/DD/YYYY HH:mm" string, the way
        // SpecialOrderDetails writes it. Updated in place when it exists, never
        // inserted. Sent only when the form has a delivery date: the row lacks
        // deliverydate in the order-detail response just like the KOT rows, so
        // resending it without one would save that column as null. With no time
        // on the form it holds just the date, so the string can never name a
        // different day from deliverydate.
        if (formDay) {
            (this.state.requestActionsRaw || [])
                .filter(r => Number(pick(r, 'actiontype', 'actionType', 'ActionType')) === 3)
                .forEach(existing => {
                    updates.push({
                        ...existing,
                        deliverydate: formDay,
                        expectedDeliveryTime: formTime ? `${formDay} ${formTime}` : formDay,
                    });
                });
        }

        return [...updates, ...inserts];
    };

    // ── One BILL row per order ──────────────────────────────────────────────
    // The bill is an order-level record: its columns are the bill header
    // totals, not a menu line, so an order carries a single BILL row
    // (actiontype 4) rather than one per item. When the order already has that
    // row it is sent back whole under its requestactionid, with the status and
    // totals refreshed; the backend updates a row by id in place, so generating
    // the bill again, or paying it, never adds a second one. Otherwise a new row
    // is built, numbered after the order's highest actionnum so it can't land on
    // another row's number. Older orders' per-item BILL rows (those carrying a
    // menuId) are left as they are.
    _buildOrderBillRow = (resStatusRow, orderHeader, billFields, requestDate) => {
        const existingActions = this.state.requestActionsRaw || [];
        const existingBill = existingActions.find(a =>
            Number(pick(a, 'actiontype', 'actionType', 'ActionType')) === 4 &&
            !pick(a, 'menuId', 'MenuId', 'menuid')
        );

        if (existingBill) {
            // The order-detail rows use the backend's lowercase column names.
            return {
                ...existingBill,
                actiontype: 4,
                statusId: resStatusRow.StatusId,
                billheadergrossamount: billFields.BillHeaderGrossAmount ?? null,
                billheaderitemdiscount: billFields.BillHeaderItemDiscount ?? null,
                billheadbilldiscount: billFields.BillHeaderBillDiscount ?? null,
                billheadtax: billFields.BillHeaderTax ?? null,
                billheadservicecharge: billFields.BillHeaderServiceCharge ?? null,
                billheadpackingcharge: billFields.BillHeaderPackingCharge ?? null,
                billheaddeliverycharge: billFields.BillHeaderDeliveryCharge ?? null,
                billheadroundoff: billFields.BillHeaderRoundOff ?? null,
                billheadgrandtotal: billFields.BillHeaderGrandTotal ?? null,
            };
        }

        const nextActionNum = existingActions.reduce(
            (max, r) => Math.max(max, Number(pick(r, 'actionnum', 'actionNum', 'Actionnum')) || 0),
            0
        ) + 1;

        return {
            ...this._buildBaseActionRow(nextActionNum, resStatusRow, resStatusRow.StatusId, requestDate),
            ...orderHeader,
            ...billFields,
        };
    };

    buildRequestActions = (actionContext, requestId, stageFields = {}, requestDate = new Date()) => {
        const menuRows = stageFields[F.MENU_ITEMS]?.ContainerFields || [];
        const orderHeader = this._buildOrderHeaderFields(stageFields);
        const billFields = this._buildBillHeaderFields(stageFields);
        const existingActions = this.state.requestActionsRaw || [];
        // existingActions rows are backend-echoed (data.requestactions), so
        // MenuId/KitchenId there can show up as MenuId, menuId, or menuid
        // depending on the endpoint — pick() covers all three. This one match
        // backs priorKotNumber, priorTargetdate and isNewMenuItem below.
        const priorAction = (row) => existingActions.find(a =>
            pick(a, 'MenuId', 'menuId', 'menuid') === row[F.MI_ITEM]?.FieldValue &&
            pick(a, 'KitchenId', 'kitchenId', 'kitchenid') === row[F.MI_KITCHEN]?.FieldValue
        );
        const priorKotNumber = (row) =>
            pick(priorAction(row) || {}, 'KOTNumber', 'kotNumber', 'kotnumber') ?? null;
        // Same carry-forward as priorKotNumber, for the same reason: once this
        // item already has a recorded action (e.g. sent to kitchen earlier),
        // re-declaring it in a later save (another item added to the same
        // running order, say) must not bump its Targetdate to "now" again —
        // only a genuinely new row (no prior match) gets today's date.
        const priorTargetdate = (row) =>
            pick(priorAction(row) || {}, 'Targetdate', 'targetDate', 'targetdate') ?? null;
        // 'NEW' the first time an item is ever declared in a KOT action row,
        // 'OLD' on every later kitchen save that re-declares it (e.g. another
        // item added to the same running order).
        const isNewMenuItem = (row) => !priorAction(row);
        // Multiple modifiers on one item go over the wire as comma-merged
        // strings ("101,102" / "2,1"), positionally paired — not arrays.
        // TESTING ONLY: the Modifier master has no real UOM data behind it yet
        // (MenuList's modal even hardcodes a placeholder price), so this stub
        // UOM id is sent whenever a modifier is selected so the backend's
        // modifier path has something non-null to work with. Remove once the
        // Modifier master carries a real UOM to read here instead.
        const TEST_MODIFIER_UOM = '10000000-0000-0000-0000-000000000005';
        const modifierActionFields = (row) => {
            const modifierIds = this.parseModifierIds(row[F.MI_MODIFIER_SEL]?.FieldValue);
            const modifierQty = row.__modifierQtys || '';
            return {
                ModifierId: modifierIds.length ? modifierIds.join(',') : null,
                ModifierQty: modifierQty ? modifierQty : null,
                ModifierUOM: modifierIds.length ? TEST_MODIFIER_UOM : null,
            };
        };

        // ── SAVE ONLY — no action rows ────────────────────────────────────────
        if (actionContext === 'save') return [];

        // ─────────────────────────────────────────────────────────────────────
        // SHARED HELPER — builds one action row per menu item.
        // resStatusRow : resolved RESSTATUS record for the action
        // extraFields  : action-specific columns to merge into every row
        // ─────────────────────────────────────────────────────────────────────
        const buildMenuItemRows = (resStatusRow, extraFields = {}) => {
            return menuRows.map((row, idx) => ({
                ...this._buildBaseActionRow(idx + 1, resStatusRow, resStatusRow.StatusId, requestDate),
                ...orderHeader,
                ...billFields,
                ...extraFields,
                // Menu item columns — always filled on every row
                MenuId: row[F.MI_ITEM]?.FieldValue || null,
                KitchenId: row[F.MI_KITCHEN]?.FieldValue || null,
                MenuItemKitchenId: row[F.MI_KITCHEN]?.FieldValue || null,
                MenuQty: row[F.MI_QTY]?.FieldValue ?? null,
                MenuUOM: row[F.MI_UOM]?.FieldValue || null,
                MenuItemRate: row[F.MI_RATE]?.FieldValue ?? null,
                MenuItemDiscountPercentage: row[F.MI_DISCOUNT_PCT]?.FieldValue ?? null,
                MenuItemTaxPercentage: row[F.MI_TAX_PCT]?.FieldValue ?? null,
                MenuItemNetAmount: row[F.MI_NET_AMOUNT]?.FieldValue ?? null,
                MenuItemSpecialInstruction: row[F.MI_SPECIAL_INSTRUCTION]?.FieldValue || null,
                MenuItemKotStatus: row[F.MI_KOT_STATUS]?.FieldValue || null,
                MenuItemServedQty: row[F.MI_SERVED_QTY]?.FieldValue ?? null,
                MenuItemPreparedQty: row[F.MI_PREPARED_QTY]?.FieldValue ?? null,
                MenuItemBalanceQty: row[F.MI_BALANCE_QTY]?.FieldValue ?? null,
                MenuItemCancelledQty: row[F.MI_CANCELLED_QTY]?.FieldValue ?? null,
                KOTNumber: priorKotNumber(row),
                Targetdate: priorTargetdate(row) || moment(requestDate).toISOString(),
                KOTInstruction: row[F.MI_SPECIAL_INSTRUCTION]?.FieldValue || null,
                ...modifierActionFields(row),
            })).sort((a, b) => {
                if (a.KitchenId < b.KitchenId) return -1;
                if (a.KitchenId > b.KitchenId) return 1;
                return a.Actionnum - b.Actionnum;
            });
        };

        // ── KITCHEN ───────────────────────────────────────────────────────────
        if (actionContext === 'kitchen') {
            // A Quick Order is handed over the counter, never cooked to order:
            // its KOT is raised already READY (14) so the kitchen has nothing
            // to do. Every other order type goes in as SENT TO KOT (11).
            const orderTypeValue = stageFields[F.ORDER_TYPE]?.FieldValue || this.props.activeOrderType;
            const isQuickOrder = String(orderTypeValue).toLowerCase() === ORDER_TYPES.QUICK_ORDER.toLowerCase();
            const kotStatus = this._requireStatus(1, isQuickOrder ? 14 : 11);
            // !r.__kotActionSent excludes items that already have a confirmed KOT
            // action row (set on rehydration or this-save-success, see
            // componentDidUpdate/saveRequest) — without it, every already-sent item
            // gets re-declared (and re-inserted, since these rows carry no
            // requestactionid) on every later Send to Kitchen for the same order.
            const sentRows = menuRows.filter(r => r[F.MI_KOT_STATUS].FieldValue === 'Sent' && !r.__kotActionSent);
            if (sentRows.length === 0) return [];

            return sentRows.map((row, idx) => ({
                ...this._buildBaseActionRow(idx + 1, kotStatus, kotStatus.StatusId, requestDate),
                ...orderHeader,
                MenuId: row[F.MI_ITEM]?.FieldValue || null,
                KitchenId: row[F.MI_KITCHEN]?.FieldValue || null,
                MenuItemKitchenId: row[F.MI_KITCHEN]?.FieldValue || null,
                MenuQty: row[F.MI_QTY]?.FieldValue ?? null,
                MenuUOM: row[F.MI_UOM]?.FieldValue || null,
                MenuItemRate: row[F.MI_RATE]?.FieldValue ?? null,
                MenuItemDiscountPercentage: row[F.MI_DISCOUNT_PCT]?.FieldValue ?? null,
                MenuItemTaxPercentage: row[F.MI_TAX_PCT]?.FieldValue ?? null,
                MenuItemNetAmount: row[F.MI_NET_AMOUNT]?.FieldValue ?? null,
                MenuItemSpecialInstruction: row[F.MI_SPECIAL_INSTRUCTION]?.FieldValue || null,
                MenuItemKotStatus: row[F.MI_KOT_STATUS]?.FieldValue || null,
                MenuItemServedQty: row[F.MI_SERVED_QTY]?.FieldValue ?? null,
                MenuItemPreparedQty: row[F.MI_PREPARED_QTY]?.FieldValue ?? null,
                MenuItemBalanceQty: row[F.MI_BALANCE_QTY]?.FieldValue ?? null,
                MenuItemCancelledQty: row[F.MI_CANCELLED_QTY]?.FieldValue ?? null,
                KOTNumber: priorKotNumber(row),
                Targetdate: priorTargetdate(row) || moment(requestDate).toISOString(),
                KOTInstruction: row[F.MI_SPECIAL_INSTRUCTION]?.FieldValue || null,
                pqejrer: isNewMenuItem(row) ? 'NEW' : 'OLD',
                // Sending to the kitchen is what queues the ticket. Only newly
                // sent items reach here - sentRows above excludes anything that
                // already carries a KOT action row - so re-saving a running
                // order cannot reprint a ticket the kitchen already has.
                printingrequired: PRINT_FLAG_ON,
                ...modifierActionFields(row),
            })).sort((a, b) => {
                if (a.KitchenId < b.KitchenId) return -1;
                if (a.KitchenId > b.KitchenId) return 1;
                return a.Actionnum - b.Actionnum;
            });
        }

        // ── DELIVERY ──────────────────────────────────────────────────────────
        if (actionContext === 'delivery') {
            const deliveryRow = stageFields[F.DELIVERY_INFO]?.ContainerFields?.[0];
            if (!deliveryRow) return [];

            const deliveryStatusMap = {
                'Out For Delivery': 31,
                'Delivered': 3999,   // DELIVERED on the live status list
                'Returned': 33,
                'Failed': 34,
                'Cancelled': 35,
            };
            const deliveryStatusValue = deliveryRow[F.DELIVERY_STATUS]?.FieldValue || 'Out For Delivery';
            const activeStatusId = deliveryStatusMap[deliveryStatusValue] || 31;
            const resStatusRow = this._requireStatus(3, activeStatusId);

            return buildMenuItemRows(resStatusRow, {
                DeliveryBoy: deliveryRow[F.DELIVERY_BOY]?.FieldValue || null,
                DeliveryCharge: deliveryRow[F.DELIVERY_CHARGE]?.FieldValue ?? null,
                ExpectedDeliveryTime: deliveryRow[F.EXPECTED_DELIVERY_TIME]?.FieldValue || null,
                Distance: deliveryRow[F.DELIVERY_DISTANCE]?.FieldValue ?? null,
                DeliveryStatus: deliveryStatusValue,
                DeliveryInstruction: deliveryRow[F.DELIVERY_INSTRUCTION]?.FieldValue || null,
                DeliveryAddress: deliveryRow[F.DELIVERY_ADDRESS]?.FieldValue || null,
                DeliveryLandmark: deliveryRow[F.DELIVERY_LANDMARK]?.FieldValue || null,
                DeliveryGoogleLocation: deliveryRow[F.DELIVERY_GOOGLE_LOCATION]?.FieldValue || null,
                DeliveryBuilding: deliveryRow[F.DELIVERY_BUILDING]?.FieldValue || null,
                DeliveryFloor: deliveryRow[F.DELIVERY_FLOOR]?.FieldValue || null,
                DeliveryDoor: deliveryRow[F.DELIVERY_DOOR]?.FieldValue || null,
            });
        }

        // ── BILL ──────────────────────────────────────────────────────────────
        if (actionContext === 'bill') {
            const billStatus = this._requireStatus(4, 41); // BILL GENERATED
            // A single order-level row; see _buildOrderBillRow.
            return [{
                ...this._buildOrderBillRow(billStatus, orderHeader, billFields, requestDate),
                // Sent explicitly rather than relying on the orderHeader spread:
                // _buildOrderBillRow's "existing bill" branch reuses the stored
                // row and does not re-apply orderHeader, so on a re-save these
                // would keep whatever the row already had. The printed bill
                // names both, so they have to be on this row.
                Waiter: stageFields[F.WAITER]?.FieldValue || null,
                Cashier: stageFields[F.CASHIER]?.FieldValue || null,
                // Generating the bill is what queues it for printing. The
                // payment context below reuses this same row and deliberately
                // leaves the flag alone, so marking an order paid never
                // reprints it.
                printingrequired: PRINT_FLAG_ON,
            }];
        }

        // ── PAYMENT ───────────────────────────────────────────────────────────
        // ── PAYMENT ───────────────────────────────────────────────────────────
        if (actionContext === 'payment') {
            const payRow = stageFields[F.PAYMENT_INFO]?.ContainerFields?.[0];
            if (!payRow) return [];

            const paymentEntries = this.state.paymentEntries || [];

            // ── Row set 1: BILL flipped to PAID (ActionId 4, StatusId 43) ────────
            // This updates the existing bill record status from BILL GENERATED → PAID
            const billPaidStatus = this._requireStatus(4, 43); // BILL → PAID

            const billPaidRows = [this._buildOrderBillRow(billPaidStatus, orderHeader, billFields, requestDate)];

            // ── Row set 2: PAYMENT rows (ActionId 5, StatusId 53) ────────────────
            // One set of menu item rows per payment entry (cash, card, gpay etc.)
            // If no payment entries fall back to a single PAID payment set
            const paymentStatus = this._requireStatus(5, 5999); // PAYMENT → PAID (live status list)
            const baseOffset = menuRows.length;  // payment rows keep the numbering they had when bill rows were one per line

            let paymentRows = [];

            if (paymentEntries.length === 0) {
                // Single payment entry — no split payment
                paymentRows = menuRows.map((row, idx) => ({
                    ...this._buildBaseActionRow(baseOffset + idx + 1, paymentStatus, paymentStatus.StatusId, requestDate),
                    ...orderHeader,
                    ...billFields,
                    MenuId: row[F.MI_ITEM]?.FieldValue || null,
                    KitchenId: row[F.MI_KITCHEN]?.FieldValue || null,
                    MenuItemKitchenId: row[F.MI_KITCHEN]?.FieldValue || null,
                    MenuQty: row[F.MI_QTY]?.FieldValue ?? null,
                    MenuUOM: row[F.MI_UOM]?.FieldValue || null,
                    MenuItemRate: row[F.MI_RATE]?.FieldValue ?? null,
                    MenuItemDiscountPercentage: row[F.MI_DISCOUNT_PCT]?.FieldValue ?? null,
                    MenuItemTaxPercentage: row[F.MI_TAX_PCT]?.FieldValue ?? null,
                    MenuItemNetAmount: row[F.MI_NET_AMOUNT]?.FieldValue ?? null,
                    MenuItemSpecialInstruction: row[F.MI_SPECIAL_INSTRUCTION]?.FieldValue || null,
                    MenuItemKotStatus: row[F.MI_KOT_STATUS]?.FieldValue || null,
                    MenuItemServedQty: row[F.MI_SERVED_QTY]?.FieldValue ?? null,
                    MenuItemPreparedQty: row[F.MI_PREPARED_QTY]?.FieldValue ?? null,
                    MenuItemBalanceQty: row[F.MI_BALANCE_QTY]?.FieldValue ?? null,
                    MenuItemCancelledQty: row[F.MI_CANCELLED_QTY]?.FieldValue ?? null,
                    KOTNumber: priorKotNumber(row),
                    Targetdate: priorTargetdate(row) || moment(requestDate).toISOString(),
                    KOTInstruction: row[F.MI_SPECIAL_INSTRUCTION]?.FieldValue || null,
                    ...modifierActionFields(row),
                    // Payment columns
                    PaymentType: payRow[F.PAY_TYPE]?.FieldValue || null,
                    PaymentAmountTendered: billFields.BillHeaderGrandTotal ?? null,
                    PaymentChangeReturn: payRow[F.PAY_CHANGE_RETURN]?.FieldValue ?? null,
                    PaymentTransactionNumber: payRow[F.PAY_TRANSACTION_NO]?.FieldValue || null,
                    PaymentCardNumber: payRow[F.PAY_CARD_NUMBER]?.FieldValue || null,
                    PaymentBank: payRow[F.PAY_BANK]?.FieldValue || null,
                    PaymentApprovalNo: payRow[F.PAY_APPROVAL_NO]?.FieldValue || null,
                    PaymentStatus: 'Paid',
                }));
            } else {
                // Split payment — one row set per entry
                paymentRows = paymentEntries.flatMap((entry, entryIdx) =>
                    menuRows.map((row, rowIdx) => ({
                        ...this._buildBaseActionRow(
                            baseOffset + entryIdx * menuRows.length + rowIdx + 1,
                            paymentStatus,
                            paymentStatus.StatusId,
                            requestDate
                        ),
                        ...orderHeader,
                        ...billFields,
                        MenuId: row[F.MI_ITEM]?.FieldValue || null,
                        KitchenId: row[F.MI_KITCHEN]?.FieldValue || null,
                        MenuItemKitchenId: row[F.MI_KITCHEN]?.FieldValue || null,
                        MenuQty: row[F.MI_QTY]?.FieldValue ?? null,
                        MenuUOM: row[F.MI_UOM]?.FieldValue || null,
                        MenuItemRate: row[F.MI_RATE]?.FieldValue ?? null,
                        MenuItemDiscountPercentage: row[F.MI_DISCOUNT_PCT]?.FieldValue ?? null,
                        MenuItemTaxPercentage: row[F.MI_TAX_PCT]?.FieldValue ?? null,
                        MenuItemNetAmount: row[F.MI_NET_AMOUNT]?.FieldValue ?? null,
                        MenuItemSpecialInstruction: row[F.MI_SPECIAL_INSTRUCTION]?.FieldValue || null,
                        MenuItemKotStatus: row[F.MI_KOT_STATUS]?.FieldValue || null,
                        MenuItemServedQty: row[F.MI_SERVED_QTY]?.FieldValue ?? null,
                        MenuItemPreparedQty: row[F.MI_PREPARED_QTY]?.FieldValue ?? null,
                        MenuItemBalanceQty: row[F.MI_BALANCE_QTY]?.FieldValue ?? null,
                        MenuItemCancelledQty: row[F.MI_CANCELLED_QTY]?.FieldValue ?? null,
                        KOTNumber: priorKotNumber(row),
                        Targetdate: priorTargetdate(row) || moment(requestDate).toISOString(),
                        KOTInstruction: row[F.MI_SPECIAL_INSTRUCTION]?.FieldValue || null,
                        ...modifierActionFields(row),
                        // Payment columns — one entry per tender method
                        PaymentType: entry.method || null,
                        PaymentAmountTendered: entry.amount ?? null,
                        PaymentChangeReturn: entryIdx === paymentEntries.length - 1
                            ? (payRow[F.PAY_CHANGE_RETURN]?.FieldValue ?? null)
                            : null,
                        PaymentTransactionNumber: entry.transactionNo || null,
                        PaymentCardNumber: entry.cardNumber || null,
                        PaymentBank: entry.bank || null,
                        PaymentApprovalNo: entry.approvalNo || null,
                        PaymentStatus: 'Paid',
                    }))
                );
            }

            // ── Return bill rows first, then payment rows ─────────────────────────
            // Backend receives them in order:
            // [BILL/PAID rows] → [PAYMENT/PAID rows]
            return [...billPaidRows, ...paymentRows];
        }

        // ── PACKING ───────────────────────────────────────────────────────────
        if (actionContext === 'packing') {
            const packingStatus = this._requireStatus(2, 21); // ASSIGNED
            const deliveryRow = stageFields[F.DELIVERY_INFO]?.ContainerFields?.[0];
            const orderType = stageFields[F.ORDER_TYPE]?.FieldValue || '';

            return buildMenuItemRows(packingStatus, {
                DeliveryBoy: orderType.toLowerCase() === 'delivery'
                    ? (deliveryRow?.[F.DELIVERY_BOY]?.FieldValue || null)
                    : null,
            });
        }

        // ── TABLE ─────────────────────────────────────────────────────────────
        // Fired the instant a free table is picked, before any menu item exists —
        // buildMenuItemRows can't be used here (it maps over menuRows, which is
        // still empty), so this is the one action row per request that isn't
        // per-item: just the table itself getting locked. ─────────────────────
        if (actionContext === 'table') {
            const tableStatus = this._requireStatus(6, 61); // TABLE LOCKED
            return [{
                ...this._buildBaseActionRow(1, tableStatus, tableStatus.StatusId, requestDate),
                ...orderHeader,
            }];
        }

        // ── TAKEAWAY ──────────────────────────────────────────────────────────
        if (actionContext === 'takeaway') {
            const takeawayStatus = this._requireStatus(9, 91); // WAITING
            return buildMenuItemRows(takeawayStatus);
        }

        return [];
    };


    // Total of the items actually sent to the kitchen — Pending (cart) rows are
    // not billable yet.
    getSentItemsTotal = () => {
        const menuRows = this.state.stageFields[F.MENU_ITEMS]?.ContainerFields || [];
        return menuRows
            .filter(row => !isTemplateRow(row) && row[F.MI_KOT_STATUS]?.FieldValue === 'Sent')
            .reduce((sum, row) => {
                const qty = Number(row[F.MI_QTY]?.FieldValue) || 0;
                const rate = Number(row[F.MI_RATE]?.FieldValue) || 0;
                return sum + qty * rate;
            }, 0);
    };

    // A Pending row is still sitting in the cart, never sent to kitchen — the
    // bill total (getSentItemsTotal) silently excludes it, so billing while
    // one exists would let that item go both uncooked and uncharged. Gates
    // handleFinish/goToBillingView below.
    hasPendingCartItems = () => {
        const menuRows = this.state.stageFields[F.MENU_ITEMS]?.ContainerFields || [];
        return menuRows.some(row => !isTemplateRow(row) && row[F.MI_KOT_STATUS]?.FieldValue === 'Pending');
    };

    isBillAlreadyGenerated = () => {
        const billRows = this.state.stageFields[F.BILL_SUMMARY]?.ContainerFields || [];
        // The ChildRowNmbr 0 template row is not a bill: it is the container's
        // blank definition row and is present on every order the moment the
        // request exists, so counting it flipped the button to "Go to Bill" on
        // orders that had never been billed — and that path shows the summary
        // without computing it, which is how the billing screen came up empty.
        return billRows.some((row) => {
            if (isTemplateRow(row)) return false;
            const grandTotal = Number(row[F.BILL_GRAND_TOTAL]?.FieldValue) || 0;
            return grandTotal > 0;
        });
    };

    goToBillingView = () => {
        // A new item added to the cart after the bill was already generated
        // must be sent to kitchen (or removed) before viewing/paying that
        // bill — otherwise it would go both uncooked and uncharged.
        if (this.hasPendingCartItems()) {
            this.growl && this.growl.show({ severity: 'error', summary: null, detail: 'Send all pending items to kitchen before going to the bill' });
            return;
        }
        const billRows = this.state.stageFields[F.BILL_SUMMARY]?.ContainerFields || [];
        const billRowIndex = billRows.findIndex(row => !isTemplateRow(row));
        const grandTotal = billRowIndex > -1
            ? Number(billRows[billRowIndex][F.BILL_GRAND_TOTAL]?.FieldValue) || 0
            : 0;

        // A summary that already has a total — manual discounts, charges and all
        // — is shown exactly as it was saved. Only an empty one gets filled from
        // the Sent items, so this screen can never open on a blank bill.
        if (billRowIndex === -1 || grandTotal > 0) {
            // Save rather than just navigate. This path runs whenever the
            // summary already carries a total, which is also when the button
            // reads "Go to Bill" instead of "Generate Bill" - so without this
            // no BILL action row was ever written, and the bill never queued
            // for printing. The 'bill' context carries printingrequired.
            this.saveRequest('submit', 'bill', () => this.setState({ view: 'billing' }));
            return;
        }

        const total = this.getSentItemsTotal();
        let stageFields = updateContainerFieldValue(
            this.state.stageFields, F.BILL_SUMMARY, billRowIndex, F.BILL_GRAND_TOTAL, total
        );
        stageFields = updateContainerFieldValue(
            stageFields, F.BILL_SUMMARY, billRowIndex, F.BILL_GROSS, total
        );
        this.setState({ stageFields }, () => {
            this.saveRequest('submit', 'bill', () => this.setState({ view: 'billing' }));
        });
    };

    // ── Fires a save that was queued (via saveRequest's re-entrancy guard) while
    // a prior save + FieldId-rehydration cycle was still in flight ──────────────
    _replayPendingSave = () => {
        const queued = this.state.pendingSave;
        if (queued) {
            this.setState({ pendingSave: null }, () => {
                this.saveRequest(queued.type, queued.redirect, queued.onSuccess);
            });
        }
    };

    saveRequest = (type, redirect = '', onSuccess = null) => {
        if (this.state.loading) {
            // ── A previous save is still persisting and rehydrating FieldIds ──
            // Firing another save now would send fields with blank FieldId again
            // (rehydration hasn't merged the real ones back into state yet), which
            // is exactly what produced duplicate rows. Queue this one instead —
            // it replays automatically once the in-flight rehydration completes
            // (see componentDidUpdate).
            this.setState({ pendingSave: { type, redirect, onSuccess } });
            return;
        }

        this.setState({ loading: true, pendingSave: null });

        const { stageFields } = this.state;
        const cartRows = stageFields[F.MENU_ITEMS].ContainerFields;
        let fieldSequence = 1;
        let RequestType = '';
        let RequestedFor = this.props.loggedUserDetail && this.props.loggedUserDetail.empID;
        let RequestDate = new Date();
        let FromDate = new Date();
        let ToDate = new Date();

        const isValidRequestId = (id) => id && id !== ZERO_GUID;

        let requestId = isValidRequestId(this.state.DynamicRequestID)
            ? this.state.DynamicRequestID
            : null;

        const isInitialEntry = !requestId;
        const actionContext = redirect || type;

        // Freshly (re)decided on every call, keyed off this call's own
        // actionContext — so a failed or abandoned 'bill' save can never leave
        // this stuck true: the next save of any kind resets it. See
        // protectBillSummaryOnce's declaration in state for what it guards.
        this.setState({ protectBillSummaryOnce: actionContext === 'bill' });

        // 'table' locks the table the instant it's picked, before any items
        // exist — the whole point is to claim it on the backend ahead of the
        // first cart item, so this is the one submit that must go through empty.
        if (type === 'submit' && actionContext !== 'table' && cartRows.length === 0) {
            this.growl && this.growl.show({ severity: 'error', summary: null, detail: 'Add at least one menu item before sending to kitchen' });
            this.setState({ loading: false });
            return;
        }

        // ── The Bill Summary row is left alone here ────────────────────────────
        // A total is written only when a bill is actually generated (handleFinish)
        // or paid (completePayment). Stamping getGrandTotal() onto the row on every
        // save put a total there on the "Send to Kitchen" save too — so the Bill
        // Summary row was already carrying an amount before the user had generated
        // anything, isBillAlreadyGenerated() read that as an existing bill, and the
        // button came up as "Go to Bill" instead of "Generate Bill". That path opens
        // the billing screen without computing the summary, which is what showed an
        // empty bill. It was also the wrong number: getGrandTotal() counts the
        // Pending cart rows that handleFinish deliberately leaves out.
        const syncedStageFields = stageFields;
        Object.values(syncedStageFields).forEach(field => {
            if (field.FieldName === raw(F.ORDER_TYPE)) RequestType = this.props.activeOrderType;
            if (field.FieldName === raw(F.REQUEST_DATE)) RequestDate = field.FieldValue;
        });


        const serializeFieldValue = (fieldValue, dataType) => {
            if (fieldValue === null || fieldValue === undefined) return fieldValue;
            if (Array.isArray(fieldValue)) return JSON.stringify(fieldValue);
            if (typeof fieldValue === 'object') return JSON.stringify(fieldValue);
            if (dataType === 'int' || dataType === 'float') return Number(fieldValue) || 0;
            return fieldValue;
        };

        // ── Builds the FieldValue actually sent for a field: date fields go through
        // moment (as LeaveRequest does), then serializeFieldValue additionally
        // JSON-stringifies arrays/objects and coerces int/float. That extra step is a
        // deliberate deviation from LeaveRequest, required by this schema — e.g. the
        // type-3 "Modifier Selection" multidropdown must serialize to "[]" and Qty
        // must go over the wire numeric. It is value formatting only, never row
        // identity, so it has no bearing on how the backend matches rows. ──────────
        const buildFieldValue = (fieldObj) => {
            if (!fieldObj) return undefined;
            const isDate = fieldObj.FieldType === 7 || fieldObj.FieldType === 8;
            const date = isDate ? toValidDate(fieldObj.FieldValue) : null;
            const raw = isDate
                // An empty or unparseable date goes up blank, never as "Invalid date".
                ? (date ? moment(date).format('MM/DD/YYYY') : '')
                : fieldObj.FieldValue;
            return serializeFieldValue(raw, fieldObj.DataType);
        };

        // fieldKeys: optional whitelist of stageFields keys — used to send just
        // one container's data (e.g. Bill Summary) instead of the full field set.
        const buildGroupAttributes = (fieldKeys = null) => {
            const groupAttributes = [];
            fieldSequence = 1;

            // Attach FieldId whenever we already have a real backend requestId
            // (i.e. this order already exists server-side), regardless of how the
            // component was loaded — route-based edit, or a rehydrated table
            // session. `this.state.type === 'edit'` only reflects route navigation
            // and misses the table-session rehydration path entirely.
            const isUpdate = isValidRequestId(requestId);

            const entries = fieldKeys
                ? fieldKeys.map(key => [key, syncedStageFields[key]]).filter(([, field]) => field)
                : Object.entries(syncedStageFields);

            entries.forEach(([, field]) => {
                const commonValues = {
                    ChildRowNmbr: field.ChildRowNmbr,
                    DataType: field.DataType,
                    FieldDescription: field.FieldLabel,
                    FieldName: field.FieldName,
                    FieldType: field.FieldType,
                    FieldValue: buildFieldValue(field),
                    ParentFieldId: field.ParentFieldId,
                    stageNum: STAGE_NUM,
                    processNum: PROCESS_NUM,
                    FieldSequence: fieldSequence++,
                    IsActive: true,
                    CompanyID: this.props.loggedUserDetail && this.props.loggedUserDetail.CompanyId,
                    StageSequence: 0,
                    ContainerType: field.type,
                    branch: this._resolveBranchId(),
                };

                if (isUpdate && field.FieldId !== undefined && field.FieldId !== '') {
                    commonValues.FieldId = field.FieldId;
                }
                if (isUpdate && field.Id) {
                    commonValues.Id = field.Id;
                }

                groupAttributes.push(commonValues);

                if (field.ContainerFields === null) return;

                // ── Template row first (ChildRowNmbr 0), then the data rows ────────
                // The template goes back untouched so the container keeps its
                // definition row on the backend, exactly as the other process pages
                // round-trip theirs.
                const containerFields = field.TemplateContainerField
                    ? [field.TemplateContainerField, ...field.ContainerFields]
                    : field.ContainerFields;

                containerFields.forEach(containerField => {
                    Object.entries(containerField)
                        .filter(([, f]) => f && typeof f === 'object' && f.FieldName)
                        .forEach(([, containerFField]) => {
                            const containerValues = {
                                // ── The row's own number, as LeaveRequest.js:1116 sends it.
                                // Never renumbered by array position: a row is identified by
                                // this value for its whole life, so the template stays 0 and a
                                // mid-list delete leaves the surviving rows where they are
                                // instead of shifting every one of them onto its neighbour's
                                // number on the next save.
                                ChildRowNmbr: containerFField.ChildRowNmbr,
                                DataType: containerFField.DataType,
                                FieldDescription: containerFField.FieldLabel,
                                FieldName: containerFField.FieldName,
                                FieldType: containerFField.FieldType,
                                FieldValue: buildFieldValue(containerFField),
                                ParentFieldId: containerFField.ParentFieldId,
                                stageNum: STAGE_NUM,
                                processNum: PROCESS_NUM,
                                FieldSequence: fieldSequence++,
                                IsActive: true,
                                CompanyID: this.props.loggedUserDetail && this.props.loggedUserDetail.CompanyId,
                                StageSequence: 0,
                                ContainerType: field.type,
                            };

                            if (isUpdate && containerFField.FieldId !== undefined && containerFField.FieldId !== '') {
                                containerValues.FieldId = containerFField.FieldId;
                            }
                            if (isUpdate && containerFField.Id) {
                                containerValues.Id = containerFField.Id;
                            }

                            groupAttributes.push(containerValues);
                        });
                });
            });

            return groupAttributes;
        };

        // ── Decide what goes into RequestAttributes and Requestactions ─────────
        //
        // A call that carries Requestactions does not repeat the field set with
        // them — the action rows are the payload in that case.
        //
        // The one exception is the very first call. On a fresh table order nobody
        // has to press Save before Send to Kitchen, so the insert that creates the
        // request is usually an action call; going up with RequestAttributes: []
        // there would create an order carrying no menu items, table or customer at
        // all. While there is no requestId yet the attributes still go with it.
        //
        // On 'save' the FULL set is sent, never a filtered subset — the same
        // shape LeaveRequest saves with, which the backend upserts on FieldId. An
        // earlier "only send changed fields" attempt also left gaps in
        // FieldSequence, since skipped fields still consumed sequence numbers.
        //
        //  actionContext  | RequestAttributes             | Requestactions
        // ────────────────┼───────────────────────────────┼──────────────────────
        //  'save'         | full attributes                | []  (no actions)
        //  'bill'         | Bill Summary only               | current action rows
        //  'payment'      | Bill Summary + Payment Info     | current action rows
        //  'kitchen'      | Menu Items, Customer Info,      | current action rows
        //                 | Table Info, order-level fields  |
        //                 | (full on the very first insert) |
        //  other actions  | [] once the request exists,     | current action rows
        //  (reject etc.)  | full on insert                  |
        //
        // 'bill', 'payment' and 'kitchen' are the exception: Requestactions only
        // carries a denormalized copy of those fields for the action/audit row
        // — it does not update the live container field values (Bill Summary,
        // Payment Information, Menu Items, Customer Info) that the next GET
        // rehydrates from. Without also sending those fields here, values just
        // written (handleFinish's total, addPayment/completePayment's rows, a
        // newly added menu item) get overwritten by stale/zero data as soon as
        // the save's response rehydrates state — leaving the billing screen
        // empty, payments missing, or a just-added item gone on reopen.
        const isSave = (actionContext === 'save');

        const requestAttributes = (isSave || isInitialEntry)
            ? buildGroupAttributes()
            : actionContext === 'bill'
                ? buildGroupAttributes([F.BILL_SUMMARY])
                : actionContext === 'payment'
                    ? buildGroupAttributes([F.BILL_SUMMARY, F.PAYMENT_INFO])
                    // Send to Kitchen: Requestactions carries the per-item KOT
                    // action rows, but the live container values (menu items,
                    // customer info, order-level fields) still need to go with
                    // it so the next GET rehydrates them correctly instead of
                    // only ever picking them up on the next explicit Save.
                    : actionContext === 'kitchen'
                        ? buildGroupAttributes([
                            F.MENU_ITEMS,
                            F.CUSTOMER_INFO,
                            F.TABLE_INFO,
                            F.ORDER_TYPE,
                            F.WAITER,
                            F.CASHIER,
                            F.SHIFT,
                            F.TOKEN_NUMBER,
                        ])
                        : [];

        // TEMPORARY: a Save of an existing delivery order also carries its KOT
        // rows' qty/rate changes and any newly added lines (buildDeliveryKotSync).
        // Decided by the order's own stored type, not the sidebar's
        // activeOrderType: an order opened from the Order List keeps its type
        // whichever tab the sidebar happens to be on.
        const orderTypeValue = syncedStageFields[F.ORDER_TYPE]?.FieldValue || this.props.activeOrderType;
        const isDeliveryUpdate = isSave && !isInitialEntry
            && String(orderTypeValue).toLowerCase() === ORDER_TYPES.DELIVERY.toLowerCase();

        // Action rows take their status from the live status master; one that
        // can't be resolved stops the save here, before anything is sent.
        let requestActions;
        try {
        requestActions = isSave
            ? (isDeliveryUpdate ? this.buildDeliveryKotSync(syncedStageFields) : [])
            // Deliberately not passing RequestDate through here — that's this
            // order's original creation timestamp (set once when the table was
            // opened, then reused unchanged on every later save, e.g. when items
            // get added to an already-running order). Each action row's own
            // Targetdate must reflect when THIS action actually happened, so this
            // leans on buildRequestActions' own default of `new Date()` (now)
            // instead of the order's stale creation date.
            : this.buildRequestActions(actionContext, requestId, syncedStageFields);

        // The save that creates the order also raises its ORDER row (ActionId 0,
        // NEW) — the same row QrOrderDetails writes when a table is opened. It
        // is the one order-level row: CaptainDashboard's Close moves it to
        // CLOSED, which is how a closed order is told from a running one.
        // A Quick Order is over the counter and done with, so its row is
        // raised already CLOSED (999) and never waits on that Close.
        const isQuickOrder = String(orderTypeValue).toLowerCase() === ORDER_TYPES.QUICK_ORDER.toLowerCase();
        const orderNewStatus = isInitialEntry ? this._requireStatus(0, isQuickOrder ? 999 : 1) : null;
        if (orderNewStatus) {
            requestActions = [
                ...requestActions,
                {
                    ...this._buildBaseActionRow(requestActions.length + 1, orderNewStatus, orderNewStatus.StatusId, new Date()),
                    ...this._buildOrderHeaderFields(syncedStageFields),
                },
            ];
        }
        } catch (err) {
            this.growl && this.growl.show({ severity: 'error', summary: null, detail: err.message });
            this.setState({ loading: false, protectBillSummaryOnce: false });
            return;
        }

        // ── Restaurant order header (REQUESTHEADER Res* columns) ──────────────
        // Denormalised copies of values that otherwise live only inside the
        // attribute containers, so an order can be listed and reported on
        // without unpacking RequestAttributes.
        //
        // Read straight off the containers rather than through
        // _buildOrderHeaderFields: that helper carries no delivery address at
        // all, and its QrOrderDetails counterpart reads F.CUSTOMER_NAME /
        // F.CUSTOMER_MOBILE, which don't exist in F (see the note above
        // _buildOrderHeaderFields) and silently resolve to undefined.
        const resCustomerRow = syncedStageFields[F.CUSTOMER_INFO]?.ContainerFields?.[0];
        const resAddressRow = syncedStageFields[F.DELIVERY_ADDRESS]?.ContainerFields?.[0];
        const resBillRow = syncedStageFields[F.BILL_SUMMARY]?.ContainerFields?.[0];
        const resPaymentRow = syncedStageFields[F.PAYMENT_INFO]?.ContainerFields?.[0];
        // A generated bill is the authoritative total — it carries the manual
        // discounts and charges the raw item sum knows nothing about. Before one
        // exists, fall back to the live order total so a running order never
        // reports 0. Same precedence goToBillingView already applies.
        const resBilledTotal = Number(resBillRow?.[F.BILL_GRAND_TOTAL]?.FieldValue) || 0;
        // Dine-in and takeaway have no delivery date of their own; the column is
        // a non-nullable DateTime, so those fall back to the order's own date.
        const resDeliveryDate = toValidDate(syncedStageFields[F.DELIVERY_DATE]?.FieldValue)
            || toValidDate(RequestDate)
            || new Date();

        const data = {
            RequestDesc: 'Restaurant Order',
            IsActive: true,
            RequestDate: moment(RequestDate).format('MM/DD/YYYY'),
            FromDate: moment(FromDate).format('MM/DD/YYYY'),
            ToDate: moment(ToDate).format('MM/DD/YYYY'),
            EmpID: this.props.loggedUserDetail && this.props.loggedUserDetail.empID,
            // LeaveRequest sends this on every save; RestaurantOrder was omitting it.
            LoginEmployeeID: this.props.loggedUserDetail && this.props.loggedUserDetail.empID,
            CompanyID: this.props.loggedUserDetail && this.props.loggedUserDetail.CompanyId,
            Remark: '',
            RequestedFor: RequestedFor,
            RequestType: RequestType,
            ProcessID: RESTAURANT_ORDER_ID,

            resdeliverydate: moment(resDeliveryDate).format('MM/DD/YYYY'),
            Resdeliverytime: syncedStageFields[F.EXPECTED_DELIVERY_TIME]?.FieldValue || '',
            Resdeliveryaddress: resAddressRow?.[F.DEL_ADDRESS]?.FieldValue || '',
            Rescustomername: resCustomerRow?.[F.CUST_NAME]?.FieldValue || '',
            Resphone: resCustomerRow?.[F.CUST_MOBILE]?.FieldValue || '',
            // A delivery order's Bill Summary grand total is stamped once, when
            // SpecialOrderDetails places the order, and never recomputed on an
            // edit — preferring it here kept sending the original total after a
            // qty/price change. On a delivery update the live item total wins.
            Restotalamount: isDeliveryUpdate
                ? this.getGrandTotal()
                : (resBilledTotal > 0 ? resBilledTotal : this.getGrandTotal()),
            Respaymentstatus: resPaymentRow?.[F.PAY_STATUS]?.FieldValue || '',

            RequestAttributes: requestAttributes,
            Requestattachments: [],
            Requestforms: [],
            Requestactions: requestActions,
            StageSequence: 0,
        };

        if (requestId != null) {
            data.RequestID = requestId;
        }

        let model = 'requestSave';
        let url = requestId
            ? '/master/api/UpdateDynamicData?model=' + model
            : '/master/api/InsertDynamicData?model=' + model;


        axios.post(url, JSON.stringify({ Key: JSON.stringify(data) }))
            .then(response => {
                // Mark rows now Sent as having a confirmed KOT action *synchronously*,
                // before any follow-up GET — the 'kitchen' branch's sentRows filter
                // relies on this to not re-declare (and re-insert) an already-sent
                // item on the next Send to Kitchen. Also protects the no-GET branch
                // below (isValidRequestId(finalRequestId) false), which has no
                // rehydration step of its own to stamp this from. No-op for any save
                // that never touched MI_KOT_STATUS.
                const stageFieldsWithKotFlag = {
                    ...syncedStageFields,
                    [F.MENU_ITEMS]: {
                        ...syncedStageFields[F.MENU_ITEMS],
                        ContainerFields: (syncedStageFields[F.MENU_ITEMS]?.ContainerFields || []).map(row => ({
                            ...row,
                            __kotActionSent: row[F.MI_KOT_STATUS]?.FieldValue === 'Sent' ? true : row.__kotActionSent,
                        })),
                    },
                };
                this.setState({ stageFields: stageFieldsWithKotFlag });
                if (response.status === 200) {
                    const returnedId = response.data?.requestId;
                    const finalRequestId = isValidRequestId(returnedId) ? returnedId : requestId;

                    if (actionContext === 'save') {
                        this.growl && this.growl.show({ severity: 'success', summary: null, detail: 'Order saved successfully' });
                    }

                    // TEMPORARY, with buildDeliveryKotSync: after a delivery order's
                    // Save, load the page fresh into this order so its KOT rows and
                    // attributes come straight from the server instead of merged
                    // local state. Goes through /pos/order/:requestid, which
                    // cold-opens the order on mount (_loadOrderFromRoute), so it
                    // works however the order was opened; a plain reload of /pos/
                    // would drop an order that only lived in component state.
                    // Runs only once this save's response has come back successful;
                    // the delay lets the success toast be read, and loading stays
                    // true until then so nothing can be edited in the gap.
                    // if (isDeliveryUpdate && isValidRequestId(finalRequestId)) {
                    //     const orderPath = '/pos/order/' + finalRequestId;
                    //     const target = this.props.history && this.props.history.createHref
                    //         ? this.props.history.createHref({ pathname: orderPath })
                    //         : orderPath;
                    //     setTimeout(() => {
                    //         if (window.location.pathname === target) window.location.reload();
                    //         else window.location.assign(target);
                    //     }, 1500);
                    //     return;
                    // }

                    // ── This table's order just got its first real request row —
                    // flip the Table Master from Locked (set the instant the table
                    // was clicked, see handleTableSelect/_resolveTableCheck) to
                    // Occupied now that there's an actual order sitting on it. ────
                    if (isInitialEntry && isValidRequestId(finalRequestId) && this.state.selectedTable) {
                        this.updateTableMasterStatus(this.state.selectedTable, TABLE_STATUS_OCCUPIED);
                    }

                    if (isValidRequestId(finalRequestId)) {
                        // ── Re-fetch and rehydrate so every field carries the FieldId the
                        // backend just assigned it. Without this, the next save has no
                        // FieldId to match existing rows against, so UpdateDynamicData
                        // inserts a brand-new set of fields instead of updating the
                        // existing ones — producing duplicate "Requested For",
                        // "Request Date", container rows, etc. on every subsequent save.
                        // Mirrors handleTableSelect's fetch-and-rehydrate flow.
                        localStorage.removeItem('Employee/RequestAttributesDetails?Id=' + finalRequestId);
                        this.setState(prev => ({
                            DynamicRequestID: finalRequestId,
                            pendingRequestId: finalRequestId,
                            loadOnce: true,
                            tableSessions: prev.selectedTable && prev.tableSessions[prev.selectedTable.tableId]
                                ? {
                                    ...prev.tableSessions,
                                    [prev.selectedTable.tableId]: {
                                        ...prev.tableSessions[prev.selectedTable.tableId],
                                        DynamicRequestID: finalRequestId,
                                        // Mirrors the Locked → Occupied flip above — only
                                        // once this table's order actually has a saved
                                        // request row does the grid badge read "Occupied".
                                        status: isInitialEntry
                                            ? 'running'
                                            : prev.tableSessions[prev.selectedTable.tableId].status,
                                    },
                                }
                                : prev.tableSessions,
                        }));
                        this.props.getMasterTableList('Employee/RequestAttributesDetails?Id=' + finalRequestId);
                    } else {
                        this.setState({ loading: false }, this._replayPendingSave);
                    }

                    if (onSuccess) onSuccess();
                } else {
                    this.setState({ loading: false }, this._replayPendingSave);
                    this.growl && this.growl.show({ severity: 'error', summary: null, detail: 'Failed' });
                }
            })
            .catch(err => {
                this.setState({ loading: false }, this._replayPendingSave);
                this.growl && this.growl.show({ severity: 'error', summary: null, detail: (err && err.response && err.response.data) || 'Submit Failed' });
            });
    };

    approveOrder = () => this.saveRequest('submit', 'kitchen');
    rejectOrder = () => this.saveRequest('reject', 'reject');
    returnOrder = () => this.saveRequest('return', 'return');

    render() {
        console.log('RestaurantOrder render', this.props.selectedBranch)
        const { activeOrderType } = this.props;
        const { view, selectedTable, tableSessions, stageFields, reqStatus, printModal } = this.state;

        // Adapt cart rows -> the { id, name, price, qty } shape CartPanel/MenuList already expect
        const cartRows = stageFields[F.MENU_ITEMS].ContainerFields;
        // id folds in the row's own index — the menu item id alone isn't a
        // unique cart-row key any more (the same dish can sit in the cart
        // twice with different modifier choices), so without this
        // OrderScreen's `cartItems.find(i => i.id === itemId)` would always
        // resolve to whichever of the two rows happens to come first.
        const cartItems = cartRows
            .map((row, idx) => ({ row, __rowIndex: idx }))
            .filter(({ row }) => row[F.MI_KOT_STATUS].FieldValue === 'Pending')
            .map(({ row, __rowIndex }) => ({
                id: `${row[F.MI_ITEM].FieldValue}__${__rowIndex}`,
                name: row.__displayName || row[F.MI_ITEM].FieldValue,
                price: Number(row[F.MI_RATE].FieldValue) || 0,   // ← coerce to number
                qty: Number(row[F.MI_QTY].FieldValue) || 0,   // ← coerce to number
                modifiersLabel: row.__modifiersLabel !== undefined
                    ? row.__modifiersLabel
                    : this.resolveModifiersLabel(row[F.MI_MODIFIER_SEL]?.FieldValue),
                __rowIndex,
            }));
        const kotItems = cartRows
            .map((row, idx) => ({ row, __rowIndex: idx }))
            .filter(({ row }) => row[F.MI_KOT_STATUS].FieldValue === 'Sent')
            .map(({ row, __rowIndex }) => ({
                id: `${row[F.MI_ITEM].FieldValue}__${__rowIndex}`,
                name: row.__displayName || row[F.MI_ITEM].FieldValue,
                price: Number(row[F.MI_RATE].FieldValue) || 0,                                    // ← coerce
                qty: Number(row[F.MI_QTY].FieldValue) || 0,                                    // ← coerce
                total: Number(row[F.MI_QTY].FieldValue) * Number(row[F.MI_RATE].FieldValue) || 0, // ← coerce
                modifiersLabel: row.__modifiersLabel !== undefined
                    ? row.__modifiersLabel
                    : this.resolveModifiersLabel(row[F.MI_MODIFIER_SEL]?.FieldValue),
                __rowIndex,
            }));

        return (
            <div className="pos-order-management">
                {this.state.loading && <div className="page-loader"></div>}
                <Toast ref={(el) => this.growl = el} />
                {this.props.error !== null && <Messages type="error" message={this.props.error} />}

                {activeOrderType === ORDER_TYPES.DINE_IN && view === 'tables' && (
                    <TableSelection
                        onSelectTable={this.handleTableSelect}
                        onReleaseTable={this.releaseLockedTable}
                        tableSessions={tableSessions}
                        runningOrders={this.state.runningOrders}
                    />
                )}

                {view === 'order' && (
                    <OrderScreen
                        orderType={activeOrderType}
                        table={selectedTable}
                        menuitems={this.props.menuitems}
                        cartItems={cartItems}
                        kotItems={kotItems}
                        onBack={activeOrderType === ORDER_TYPES.DINE_IN ? this.handleBackToTables : null}
                        onAddToCart={this.addToCart}
                        onUpdateQty={(rowIndex, delta) => this.updateQty(rowIndex, delta)}
                        onRemoveFromCart={(rowIndex) => this.removeFromCart(rowIndex)}
                        onSendToKitchen={this.sendToKitchen}
                        sendingToKitchen={this.state.loading}
                        onSave={(type) => this.saveRequest(type, 'save')}
                        onCloseTable={this.closeTable}
                        onFinish={this.handleFinish}
                        grandTotal={this.getGrandTotal()}
                        sessionStartTime={
                            selectedTable && tableSessions[selectedTable.id]
                                ? tableSessions[selectedTable.id].startTime
                                : null
                        }
                        // Schema-backed fields, replacing the old local orderInfo/customerInfo shape.
                        // See OrderScreen.js for the field-by-field wiring (Table/Customer/Delivery containers).
                        stageFields={stageFields}
                        onUpdateField={this.updateField}
                        onUpdateContainerField={this.updateContainerField}

                        // Dropdown option sources - these were fetched in componentDidMount
                        // and mapped in mapStateToProps, but were missing here before,
                        // which is why the Employee/Branch/etc dropdowns rendered empty.
                        Employee={this.props.Employee}
                        Branch={this.props.Branch}
                        Shift={this.props.Shift}
                        MenuItem={this.props.MenuItem}
                        UOM={this.props.UOM}
                        Kitchen={this.props.Kitchen}
                        DeliveryBoy={this.props.DeliveryBoy}
                        PaymentType={this.props.PaymentType}
                        ActionStatus={this.props.ActionStatus}
                        TableStatus={this.props.TableStatus}
                        printKOT={this.printKOT}
                        printBill={this.printBill}
                        isBillGenerated={this.isBillAlreadyGenerated()}
                        onGoToBill={this.goToBillingView}

                    />
                )}

                {view === 'billing' && (
                    <BillingScreen
                        stageFields={stageFields}
                        onUpdateContainerField={this.updateContainerField}
                        onBack={this.backToOrderFromBilling}
                        onCompletePayment={this.completePayment}
                        submitting={this.state.billingSubmitting}
                        printBill={this.printBill}
                        PaymentType={this.props.PaymentType}
                        onAddPayment={this.addPayment}
                        onPayWithRazorpay={this.payWithRazorpay}
                        requestId={
                            this.state.DynamicRequestID && this.state.DynamicRequestID !== ZERO_GUID
                                ? this.state.DynamicRequestID
                                : null
                        }
                    />
                )}

                {reqStatus === 'SentToKitchen' && (
                    <div className="p-col-12 restaurant-order-actions">
                        <Button className="p-button-success ml1" label="Kitchen Confirm / Approve" icon="pi pi-check" onClick={this.approveOrder} />
                        <Button className="p-button-danger ml1" label="Reject" icon="pi pi-times" onClick={this.rejectOrder} />
                        <Button className="p-button-warning ml1" label="Return" icon="pi pi-replay" onClick={this.returnOrder} />
                    </div>
                )}

                <PrintPreviewModal
                    visible={!!printModal}
                    type={printModal?.type}
                    data={printModal?.data}
                    onHide={this.closePrintModal}
                    // Bill only. Passing nothing for a KOT leaves that path on
                    // the browser print dialog, exactly as it is today.
                    onPrint={printModal?.type === 'bill' ? this.queueBillPrint : undefined}
                />
            </div>
        );
    }
}

const mapStateToProps = state => {
    console.log(state);
    return {
        error: state.master.error,
        loading: state.master.loading,
        success: state.master.success,
        loggedUserDetail: state.auth.userObject,
        tableLists: state.master.tableLists,
        Branch: (state.keyword.Branch !== undefined) ? state.keyword.Branch : [],
        Employee: (state.keyword.Employee !== undefined) ? state.keyword.Employee : [],
        Shift: (state.keyword.Shift !== undefined) ? state.keyword.Shift : [],
        MenuItem: (state.keyword.MenuItem !== undefined) ? state.keyword.MenuItem : [],
        UOM: (state.keyword.UOM !== undefined) ? state.keyword.UOM : [],
        Kitchen: (state.keyword.Kitchen !== undefined) ? state.keyword.Kitchen : [],
        DeliveryBoy: (state.keyword.DeliveryBoy !== undefined) ? state.keyword.DeliveryBoy : [],
        PaymentType: (state.keyword.PaymentType !== undefined) ? state.keyword.PaymentType : [],
        ActionStatus: (state.keyword.ActionStatus !== undefined) ? state.keyword.ActionStatus : [],
        TableStatus: (state.keyword.TableStatus !== undefined) ? state.keyword.TableStatus : [],

        menuitems: state.master.masterData.GetinvMenuItemList,
        tables: state.master.masterData.GetresTableList,
        selectedBranch: state.master.selectedBranch,
        Modifier: (state.keyword.Modifier !== undefined) ? state.keyword.Modifier : [],
    };
};

const mapDispatchToProps = dispatch => {
    return {
        getMasterKeywordList: (value) => dispatch(actions.getMasterKeywordList(value)),
        getMasterTableList: (table) => dispatch(actions.getMasterTableList(table)),
        getMasterTableListCache: (table) => dispatch(actions.getMasterTableListCache(table)),
        clearCacheStorage: (type, data) => dispatch(actions.clearCacheStorage(type, data)),
        getMasterData: (tableUrl, companyId) => dispatch(actions.getMasterData(tableUrl, companyId)),
        processDynamicData: (model, type, data, func) => dispatch(actions.processDynamicData(model, type, data, func)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(RestaurantOrder);